<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="order-details">
      <div class="row">
				<div class="col-md-12">
          <div class="order-title-row flex-row space-between">
            <h3 v-if="isMainOrder">{{ _('Order') }} {{ order.id }}<template v-if="paymentPlan"> / {{ _('Summary') }}</template></h3>
            <h3 v-else-if="isAdvanceOrder">{{ _('Order') }} {{ mainOrderId }} / {{ _('Advance payment') }}</h3>
            <h3 v-else-if="isFinalOrder">{{ _('Order') }} {{ mainOrderId }} / {{ _('Final payment') }}</h3>
            <div class="order-title-buttons flex-row space-between">
              <template v-if="!paymentPlan && order.payment === 'open' && !order.subscription_renewal && isFeatureEnabled('paymentplans')">
                <button type="button" class="btn btn-primary" v-on:click="selectPaymentPlanDialog" :disabled="!isModifyOrderEnabled"><i class="fa fa-fw fa-calendar-check"></i> {{ _('Create payment plan') }}</button>
              </template>
              <button class="btn btn-primary" type="button" v-on:click="modifyOrderAsNew" v-if="isModifyConfirmedOrderAvailable" :disabled="!isModifyOrderEnabled"><i class="fa fa-fw fa-pencil-square-o"></i> {{ _('Edit as a new order') }}</button>
              <button class="btn btn-primary" type="button" v-on:click="modifyOrder" v-else-if="isModifyOrderAvailable" :disabled="!isModifyOrderEnabled"><i class="fa fa-fw fa-pencil-square-o"></i> {{ _('Edit order') }}</button>
            </div>
          </div>
          <p class="text-muted" v-if="order.subscription_renewal"><i class="fa fa-fw fa-rotate-right"></i> {{ _('This order bills a period of a subscription. To change what is billed, edit the subscription.') }}</p>
        </div>
      </div>
      <editor-tabs v-if="paymentPlan && paymentPlan.type === 'advance'" id="secondaryTabNavi" style="margin-bottom: 5px" :label="_('Select order to view')" :tabs="planOrderTabs" :selected="selectedPlanOrderTab" v-on:select="openPlanOrder" />
      <editor-tabs v-else-if="folioOrders.length > 1" id="secondaryTabNavi" style="margin-bottom: 5px" :label="_('Select order to view')" :tabs="folioOrderTabs" :selected="order ? String(parseInt(order.id)) : null" v-on:select="openFolioTab" />
      <lock-info v-if="isAnonymizedRecord" :locked="true" :lockStatus="anonymizedLockStatus" :readOnly="true" />
      <lock-info v-else-if="isOrderLocked" :locked="isOrderLocked" :lockStatus="orderLockStatus" v-on:forceLock="unlockOrder()" />
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="order-tags-row hidden-print">
              <div class="order-tags-title">
                <h3>{{ _('Tags') }}</h3>
              </div>
              <div class="order-tags-selector">
                <div class="form-group">
                  <v-select :options="allSelectableTags" v-model="tagToAdd" v-on:option:selected="addTag" :taggable="true" label="title" keys="['title']" :placeholder="_('Type to search or add tags')">
                    <template v-slot:no-options="{ searching }">
                      <template v-if="searching">
                        {{ _('No results found.') }}
                      </template>
                      <template v-else>{{ _('Type to search or add tags') }}</template>
                    </template>
                  </v-select>
                </div>
              </div>
              <div class="order-tags-selected">
                <div class="tags-list flex-row">
                  <span v-for="tag in tags" class="btn btn-info" v-on:click="removeTag(tag)"><i class="fa fa-fw fa-close"></i> {{ getTagTitle(tag) }}</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="order-items" v-if="allItems && allItems.length">
                  <div class="table-responsive">
                    <table class="table table-striped products-table">
                      <thead>
                        <tr>
                          <th>{{ _('ID') }}</th>
                          <th>{{ _('Product name') }}</th>
                          <th>{{ _('Amount') }}</th>
                          <th class="hidden-print">{{ _('Unit price (inc. vat)') }}</th>
                          <th class="hidden-print">{{ _('Total (inc. vat)') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(item, itemIndex) in topLevelItems" :key="itemIndex + '-' + item.id">
                          <component :is="getItemComponent(item)" :item="item" v-on:reservationClicked="displayReservation" v-on:valuecardClicked="displayValuecard" />
                          <tr v-if="item.packages && !packageExpanded[item.id] && getItemOptions(item).length">
                            <td class="participant-title"><button class="btn btn-info btn-xs" v-on:click="expandPackage(item)"><i class="fa fa-fw fa-plus"></i></button></td>
                            <td colspan="3" class="participant-title"><strong>{{ _('Packaged products') }}</strong></td>
                            <td class="participant-title">{{ $filters.currency(getPackageOptionPrice(item), settings.currency) }}</td>
                          </tr>
                          <template v-else-if="item.packages && !getItemOptions(item).length">
                            <template v-for="(pkg, pkgIndex) in getParsedPackages(item)">
                              <tr>
                                <td colspan="5" class="participant-title">
                                  <h4>{{ pkg.name }} <button v-if="isModifyOrderAvailable" v-on:click="editParticipantDialog(item, pkgIndex)" class="btn btn-success btn-xs"><i class="fa fa-fw fa-pencil"></i></button></h4>
                                </td>
                              </tr>
                              <tr v-for="prod in pkg.products">
                                <td>&nbsp;</td>
                                <td>
                                  <div class="btn-group">
                                    <a href="javascript:;" class="btn btn-sm btn-info"><template v-for="rp in prod.resourcePools"><i class="fa fa-fw fa-sitemap"></i> {{ rp.id }}: {{ rp.name }}</template></a>
                                  </div>
                                </td>
                                <td>{{ prod.count }}</td>
                                <td colspan="2">&nbsp;</td>
                              </tr>
                            </template>
                          </template>
                          <template v-for="option in getItemOptions(item)">
                            <tr v-if="option.participant && isOptionExpanded(item, option)">
                              <td colspan="5" class="participant-title">
                                <h4>{{ option.participant }} <button v-if="isModifyOrderAvailable" v-on:click="editParticipantDialog(item, option)" class="btn btn-success btn-xs"><i class="fa fa-fw fa-pencil"></i></button> <button v-if="isModifyOrderAvailable" v-on:click="removeParticipantDialog(item, option)" class="btn btn-danger btn-xs"><i class="fa fa-fw fa-trash"></i></button></h4>
                              </td>
                            </tr>
                            <component v-if="isOptionExpanded(item, option)" :is="getItemComponent(option)" :is-option="true" :key="option.id" :item="option" :parent-item="item" v-on:reservationClicked="displayReservation" v-on:valuecardClicked="displayValuecard" />
                          </template>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="folio-toolbar hidden-print" v-if="isFolioMoveAvailable && !folioSelectMode">
                  <button class="btn btn-default btn-sm" type="button" v-on:click="startFolioSelect"><i class="fa fa-fw fa-code-fork"></i> {{ _('Split into folios') }}</button>
                </div>
                <div class="panel panel-default folio-panel hidden-print" v-if="isFolioMoveAvailable && folioSelectMode">
                  <div class="panel-heading folio-panel-heading">
                    <strong>{{ _('Select items to move') }}</strong>
                    <button class="btn btn-link btn-xs" type="button" v-on:click="cancelFolioSelect" :aria-label="_('Close')"><i class="fa fa-fw fa-close"></i></button>
                  </div>
                  <ul class="list-group folio-item-list">
                    <li v-for="item in folioSelectableItems" :key="'folio-' + item.id" :class="'list-group-item folio-item' + (isFolioItemSelected(item) ? ' folio-item-selected' : '')" role="checkbox" :aria-checked="isFolioItemSelected(item)" tabindex="0" v-on:click="toggleFolioItem(item)" v-on:keydown.enter.prevent="toggleFolioItem(item)" v-on:keydown.space.prevent="toggleFolioItem(item)">
                      <input type="checkbox" tabindex="-1" aria-hidden="true" :checked="isFolioItemSelected(item)">
                      <span class="folio-item-name">{{ item.product_name }}<small class="text-muted" v-if="parseFloat(item.count) > 1"> &times; {{ item.count }}</small></span>
                      <span class="folio-item-price">{{ $filters.currency(folioItemTotal(item), settings.currency) }}</span>
                    </li>
                  </ul>
                  <div class="panel-footer folio-panel-footer">
                    <span class="folio-summary">{{ _('Selected') }}: <strong>{{ selectedFolioItems.length }}</strong><template v-if="selectedFolioItems.length"> &middot; {{ $filters.currency(selectedFolioTotal, settings.currency) }}</template><small class="text-warning" v-if="selectedFolioItems.length && !folioLeavesItemBehind"> &middot; {{ _('Leave at least one item on this order') }}</small></span>
                    <span class="folio-actions">
                      <button class="btn btn-default btn-sm" type="button" v-on:click="cancelFolioSelect">{{ _('Cancel') }}</button>
                      <button class="btn btn-primary btn-sm" type="button" :disabled="!selectedFolioItems.length || !folioLeavesItemBehind" v-on:click="moveToFolioDialog"><i class="fa fa-fw fa-share"></i> {{ _('Move to folio') }}</button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="button-row" v-if="isResendBarVisible">
              <button v-if="hasActiveValueCards" class="btn btn-primary" type="button" v-on:click="resendValueCardEmailDialog"><i class="fa fa-fw fa-retweet"></i> {{ _('Resend value card email') }}</button>
              <button v-else-if="hasValueCards && hasInvoice" class="btn btn-primary" type="button" v-on:click="activateValueCardsDialog"><i class="fa fa-fw fa-check-to-slot"></i> {{ _('Activate value cards') }}</button>
              <button v-if="hasTickets" class="btn btn-primary" type="button" v-on:click="resendTicketEmailDialog"><i class="fa fa-fw fa-retweet"></i> {{ _('Resend ticket email') }}</button>
            </div>
            <div class="row" v-if="isBroken">
              <div class="col-md-12">
                <div class="alert alert-warning">{{ _('It appears something has gone wrong with the order process.') }} {{ _('You can convert the order to an open order to continue.') }}<br /><br /><button class="btn btn-warning" type="button" v-on:click="convertToOpenOrder"><i class="fa fa-fw fa-wrench"></i> {{ _('Convert to open order') }}</button></div>
              </div>
            </div>
            <div class="row" v-if="showConvertToOpenOrder && !isBroken">
              <div class="col-md-12">
                <div class="alert alert-info">
                  {{ _('The order has been confirmed and can no longer be edited.') }}
                  {{ _('You can cancel the confirmation and convert the order to an open order.') }}
                  <br/><br/>
                  <button class="btn btn-info" type="button" v-on:click="convertToOpenOrder"><i class="fa fa-exchange"></i> {{ _('Convert to open order') }}</button>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-6 rowspacing">
                <h3>{{ _('Summary') }}</h3>
                <table class="table">
                  <tbody>
                    <tr v-if="parseInt(order.delivery)">
                      <td width="50%">{{ _('Delivery method') }}</td>
                      <td>{{ deliveryName }}<span class="hidden-print">, {{ $filters.currency(order.totalWithDelivery - order.total, settings.currency) }}</span></td>
                    </tr>
                    <tr class="hidden-print">
                      <td width="50%">{{ _('Total (vat 0%)') }}</td>
                      <td>{{ $filters.currency(order.totalWithDeliveryVatFree, settings.currency) }}</td>
                    </tr>
                    <tr class="hidden-print">
                      <td width="50%">{{ _('Total vat') }}</td>
                      <td>{{ $filters.currency(order.totalWithDeliveryVat, settings.currency) }}</td>
                    </tr>
                    <tr class="hidden-print" v-for="(vat, perc) in order.vatbreakdown">
                      <td width="50%">{{ $filters.num(perc, 2, false) }}%</td>
                      <td>{{ $filters.currency(vat, settings.currency) }}</td>
                    </tr>
                    <tr class="hidden-print">
                      <td width="50%">{{ _('Final price') }}</td>
                      <td>{{ $filters.currency(order.totalWithDelivery, settings.currency) }}</td>
                    </tr>
                    <template v-if="hasPaymentPlan">
                      <tr>
                        <td width="50%">{{ _('Payment plan') }}</td>
                        <td>{{ getPaymentPlanType(paymentPlan.type) }}</td>
                      </tr>
                      <template v-if="paymentPlan.type === 'advance'">
                        <tr>
                          <td width="50%">{{ _('Payment plan due') }}</td>
                          <td>{{ DateTime.fromISO(paymentPlan.final_payment_date).toFormat(dateFormat) }}</td>
                        </tr>
                        <tr>
                          <td width="50%">{{ _('Initial payment amount') }}</td>
                          <td>{{ $filters.currency(paymentPlan.amount, settings.currency) }}</td>
                        </tr>
                        <tr>
                          <td width="50%">{{ _('Final payment amount') }}</td>
                          <td>{{ $filters.currency(paymentPlan.total_amount - paymentPlan.amount, settings.currency) }}</td>
                        </tr>
                      </template>
                    </template>
                    <tr :class="orderStatusClass">
                      <td width="50%">{{ _('Status') }}</td>
                      <td>{{ orderStatus }}</td>
                    </tr>
                    <tr v-if="order.payment">
                      <td width="50%">{{ _('Payment method') }}</td>
                      <td>{{ paymentName }}</td>
                    </tr>
                    <tr v-if="bamboraId">
                      <td width="50%">{{ _('Visma Pay order number') }}</td>
                      <td>{{ bamboraId }}</td>
                    </tr>
                    <tr v-if="parseInt(order.groupdeal)">
                      <td width="50%">{{ _('Group deal') }}</td>
                      <td>{{ _('Yes') }}</td>
                    </tr>
                    <tr v-if="parseInt(order.paymentlinksent)">
                      <td width="50%">{{ _('Payment link sent') }}</td>
                      <td>{{ _('Yes') }}</td>
                    </tr>
                    <tr v-if="parseInt(order.confirmationlinksent)">
                      <td width="50%">{{ _('Confirmation link sent') }}</td>
                      <td>{{ _('Yes') }}</td>
                    </tr>
                    <tr v-if="isPaymentLinkVisible">
                      <td colspan="2">
                        <div class="flex-row">
                          <div>
                            <a class="btn btn-primary hidden-print" id="paymentLinkButton" v-on:click="displayPaymentLink"><i class="fa fa-credit-card fa-fw"></i> {{ _('Payment link') }}</a>
                            <span style="display: inline-block; margin-left: 10px" v-if="order.expirationdate">{{ _('Valid until') }} {{ $filters.timestampToDate(order.expirationdate) }}</span>
                          </div>
                          <a class="btn btn-info hidden-print" id="confirmationLinkButton" v-if="showConfirmationLink" v-on:click="displayConfirmationLink"><i class="fa fa-check-square-o fa-fw"></i> {{ _('Confirmation link') }}</a>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <h3>{{ _('Order details') }}</h3>
                <table class="table">
                  <tbody>
                    <tr v-if="order.version">
                      <td width="50%">{{ _('Language') }}</td>
                      <td>
                        <select class="form-control" v-model="order.version">
                          <option v-for="version in allversions" :value="version">{{ getVersionName(version) }}</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td width="50%">{{ _('Order date') }}</td>
                      <td>{{ $filters.timestampToDate(order.orderdate) }} {{ _('at') }} {{ $filters.timestampToTime(order.orderdate) }}</td>
                    </tr>
                    <tr>
                      <td width="50%">{{ _('Order number') }}</td>
                      <td>{{ order.id }}</td>
                    </tr>
                    <tr v-if="order.reference">
                      <td width="50%">{{ _('Reference') }}</td>
                      <td>{{ order.reference }}</td>
                    </tr>
                    <tr v-if="order.yourreference">
                      <td width="50%">{{ _('Your reference') }}</td>
                      <td>{{ order.yourreference }}</td>
                    </tr>
                    <tr v-if="order.mark">
                      <td width="50%">{{ _('Mark') }}</td>
                      <td>{{ order.mark }}</td>
                    </tr>
                    <tr v-if="order.src">
                      <td width="50%">{{ _('Order source page') }}</td>
                      <td style="word-break: break-all;"><a v-if="/^https?:\/\//i.test(order.src)" target="_blank" rel="noopener noreferrer" :href="order.src">{{ order.src }}</a><template v-else>{{ order.src }}</template></td>
                    </tr>
                    <tr v-if="order.origin">
                      <td width="50%">{{ _('Order source channel') }}</td>
                      <td style="word-break: break-all">{{ formattedOrigin }}<span v-if="order.pos_name"> / {{ order.pos_name }}</span></td>
                    </tr>
                    <tr>
                      <td width="50%">{{ _('Tracking code') }}</td>
                      <td><input type="text" class="form-control" id="trackingCode" name="trackingcode" v-model="order.trackingcode" :placeholder="_('Enter tracking code')" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="col-sm-6">
                <template v-if="!customerDetailsEditable">
                  <h3>{{ _('Customer details') }}</h3>
                  <p><strong>{{ order.name }}</strong></p>
                  <span v-if="order.ident">{{ order.ident }}<br /></span>
                  <p>
                    <span v-if="order.address">{{ order.address }}<br/></span>
                    <span v-if="order.zip || order.city">{{ order.zip }} {{ order.city }}<br /></span>
                    <span v-if="order.country">{{ getCountryName(order.country) }}<br /></span>
                    <span v-if="order.phone">{{ _('tel.') }} {{ order.phone }}<br/></span>
                    <span v-if="order.company">{{ order.company }}<br/></span>
                    <span v-if="order.evoice">{{ _('E-Invoice Address') }}: {{ order.evoice }}<br /></span>
                    <span v-if="order.evoiceint">{{ _('E-Invoice Intermediary') }}: {{ order.evoiceint }}<br/></span>
                    <span v-if="order.email">{{ _('Email') }}: <a :href="'mailto:' + order.email">{{ order.email }}</a></span>
                  </p>
                  <span v-if="order.more">
                    <h3>{{ _('Additional info') }}</h3>
                    <p style="white-space: pre-line">{{ order.more }}</p>
                  </span>
                  <div v-if="verifiedCustomer" class="well">
                    <h4 style="margin-top: 0"><i class="fa fa-fw fa-check-square-o"></i> {{ _('Verified customer') }}</h4>
                    <span>{{ verifiedCustomer.name }}<br /></span>
                    <span>{{ verifiedCustomer.dateOfBirth }}<br /></span>
                    <span>{{ verifiedCustomer.nin.issuingCountry }}<br /></span>
                    <span style="font-size: 10px">{{ _('Verified at') }}: {{ $filters.timestampToDate(verifiedCustomer.ts) }} {{ $filters.timestampToTime(verifiedCustomer.ts) }}</span>
                    <span style="font-size: 10px">/ ID: {{ verifiedCustomer.id }}</span>
                  </div>
                  <div class="flex-row">
                    <a class="btn btn-primary hidden-print" v-if="!isAnonymizedRecord" v-on:click="editCustomerDetails"><i class="fa fa-pencil-square-o fa-fw"></i> {{ _('Edit') }}</a>
                    <a class="btn btn-primary hidden-print" v-on:click="displayCustomer" v-if="order.customer_id"><i class="fa fa-fw fa-male"></i> {{ _('Customer') }}</a>
                  </div>
                </template> 
                <template v-else>
                  <h3>{{ _('Customer details') }}</h3>
                  <p><input type="text" class="form-control" v-model="order.name" :placeholder="_('Name')" id="customerName" /></p>
                  <p><input type="text" class="form-control" v-model="order.ident" :placeholder="_('Personal ID Number') + ' / ' + _('Business ID')" id="customerIdent" /></p>
                  <p><input type="text" class="form-control" v-model="order.address" :placeholder="_('Street address')" id="customerAddress" /></p>
                  <p><input type="text" class="form-control" v-model="order.zip" :placeholder="_('Zip code')" id="customerZip" /></p>
                  <p><input type="text" class="form-control" v-model="order.city" :placeholder="_('City')" id="customerCity" /></p>
                  <p>
                  <select :class="'form-control' + (!order.country ? ' no-value' : '')" v-model="order.country" id="customerCountry" :title="_('Country')">
                    <option disabled selected data-hidden="true" :value="null">{{ _('Country') }}</option>
                    <option :value="primaryCountry.id">{{ primaryCountry.country }}</option>
                    <option v-for="(country, id) in priorityCountries" :value="id">{{ country }}</option>
                    <option class="select-separator" disabled="disabled">────────────</option>
                    <option v-for="(country, id) in countries" :value="id">{{ country }}</option>
                  </select>
                  </p>
                  <p><input type="text" class="form-control" v-model="order.phone" :placeholder="_('Phone')" id="customerPhone" /></p>
                  <p><input type="text" class="form-control" v-model="order.company" :placeholder="_('Company')" id="customerCompany" /></p>
                  <p><input type="text" class="form-control" v-model="order.evoice" :placeholder="_('E-Invoice Address')" id="customerEvoice" /></p>
                  <p><input type="text" class="form-control" v-model="order.evoiceint" :placeholder="_('E-Invoice Intermediary')" id="customerEvoiceInt" /></p>
                  <p><input type="text" class="form-control" v-model="order.yourreference" :placeholder="_('Your reference')" id="customerYourReference" /></p>
                  <p><input type="text" class="form-control" v-model="order.mark" :placeholder="_('Mark')" id="customerMark" /></p>
                  <p><input type="text" class="form-control" v-model="order.email" :placeholder="_('Email')" id="customerEmail" /></p>
                  <p><textarea class="form-control" wrap="virtual" v-model="order.more" :placeholder="_('Additional info')" id="customerMore"></textarea></p>
                  <a class="btn btn-primary hidden-print" v-on:click="closeCustomerDetails"><i class="fa fa-check fa-fw"></i> {{ _('Ok') }}</a>
                </template>
                <template v-if="!billingCustomerDetailsEditable">
                  <h3>{{ _('Billing info') }}</h3>
                  <template v-if="hasBillingCustomer">
                    <p v-if="order.billing_company"><strong>{{ order.billing_company }}</strong></p>
                    <span v-if="order.billing_department">{{ order.billing_department }}<br /></span>
                    <span v-if="order.billing_ident">{{ order.billing_ident }}<br /></span>
                    <p>
                      <span v-if="order.billing_address">{{ order.billing_address }}<br /></span>
                      <span v-if="order.billing_zip || order.billing_city">{{ order.billing_zip }} {{ order.billing_city }}<br /></span>
                      <span v-if="order.billing_country">{{ getCountryName(order.billing_country) }}<br /></span>
                      <span v-if="order.billing_phone">{{ _('tel.') }} {{ order.billing_phone }}<br /></span>
                      <span v-if="order.billing_name">{{ order.billing_name }}<br /></span>
                      <span v-if="order.billing_evoice">{{ _('E-Invoice Address') }}: {{ order.billing_evoice }}<br /></span>
                      <span v-if="order.billing_evoiceint">{{ _('E-Invoice Intermediary') }}: {{ order.billing_evoiceint }}<br/></span>
                      <span v-if="order.billing_billingemail">{{ _('Billing Email') }}: <a :href="'mailto:' + order.billing_billingemail">{{ order.billing_billingemail }}</a><br /></span>
                      <span v-if="order.billing_email">{{ _('Email') }}: <a :href="'mailto:' + order.billing_email">{{ order.billing_email }}</a></span>
                    </p>
                    <div class="flex-row">
                      <a class="btn btn-primary hidden-print" v-if="!isAnonymizedRecord" v-on:click="editBillingCustomerDetails"><i class="fa fa-pencil-square-o fa-fw"></i> {{ _('Edit') }}</a>
                      <a class="btn btn-primary hidden-print" v-on:click="displayBillingCustomer" v-if="order.billingcustomer_id"><i class="fa fa-fw fa-building-o"></i> {{ _('Billing Customer') }}</a>
                    </div>
                  </template>
                  <p v-else><strong>{{ _('No billing customer set, using customer details.') }}</strong></p>
                </template>
                <template v-else>
                  <h3>{{ _('Billing info') }}</h3>
                  <p><input type="text" class="form-control" v-model="order.billing_company" :placeholder="_('Company')" id="billingCustomerCompany" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_department" :placeholder="_('Department')" id="billingCustomerDepartment" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_ident" :placeholder="_('Personal ID Number') + ' / ' + _('Business ID')" id="billingCustomerIdent" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_address" :placeholder="_('Street address')" id="billingCustomerAddress" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_zip" :placeholder="_('Zip code')" id="billingCustomerZip" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_city" :placeholder="_('City')" id="billingCustomerCity" /></p>
                  <p>
                  <select class="form-control" v-model="order.billing_country" id="billingCustomerCountry">
                    <option v-for="(country, id) in countries" :value="id" v-if="id == 'FI'">{{ country }}</option>
                    <option v-for="(country, id) in countries" :value="id" v-if="isPriorityCountry(id)">{{ country }}</option>
                    <option class="select-separator" disabled="disabled">────────────</option>
                    <option v-for="(country, id) in countries" :value="id">{{ country }}</option>
                  </select>
                  </p>
                  <p><input type="text" class="form-control" v-model="order.billing_phone" :placeholder="_('Phone')" id="billingCustomerPhone" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_name" :placeholder="_('Name')" id="billingCustomerName" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_evoice" :placeholder="_('E-Invoice Address')" id="billingCustomerEvoice" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_evoiceint" :placeholder="_('E-Invoice Intermediary')" id="billingCustomerEvoiceInt" /></p>
                  <p><input type="text" class="form-control" v-model="order.yourreference" :placeholder="_('Your reference')" id="customerYourReference" /></p>
                  <p><input type="text" class="form-control" v-model="order.mark" :placeholder="_('Mark')" id="customerMark" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_billingemail" :placeholder="_('Billing Email')" id="billingCustomerBillingEmail" /></p>
                  <p><input type="text" class="form-control" v-model="order.billing_email" :placeholder="_('Email')" id="billingCustomerEmail" /></p>
                  <a class="btn btn-primary btn-margin-bottom hidden-print" v-on:click="closeBillingCustomerDetails"><i class="fa fa-check fa-fw"></i> {{ _('Ok') }}</a>
                </template>
              </div>
            </div>
            <h3 v-if="isRefunded">{{ _('The order has been refunded!') }}</h3>
            <hr v-if="order.identifier"/>
            <div class="button-row" v-if="order.identifier">
              <a class="btn btn-info" :href="orderInfoUrl" v-if="orderInfoUrl" target="_blank"><i class="fa fa-globe fa-fw"></i> {{ _('Order information page') }}</a>
              <a class="btn btn-primary" v-if="isPrintReceiptVisible" v-on:click="printReceipt"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF receipt') }}</a>
              <a class="btn btn-primary" v-if="isPrintInvoiceVisible" v-on:click="printInvoice"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF invoice') }}</a>
              <a class="btn btn-primary" v-if="isPrintTransferVisible" v-on:click="printTransfer"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF bank transfer') }}</a>
              <a class="btn btn-primary" v-if="isPrintDeliveryManifestVisible" v-on:click="printDeliveryManifest"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF delivery manifest') }}</a>
              <a class="btn btn-primary" v-if="isRefunded" v-on:click="printRefund"><i class="fa fa-file-pdf fa-fw"></i> {{ _('PDF refund receipt') }}</a>
              <template v-if="isRefundVisible">
                <Dropdown buttonClass="btn-danger" direction="up" v-if="hasVismaPayPayments || hasVivaPayments || hasMobilepayPayments || hasPaytrailPayments">
                  <template #trigger>
                    <i class="fa fa-fw fa-undo"></i> {{ _('Refund transaction') }} &nbsp;<span class="caret"></span>
                  </template>
                  <li><a v-if="hasVismaPayPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and Visma Pay payments') }} <i></i></a></li>
                  <li><a v-if="hasVivaPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and Viva payments') }} <i></i></a></li>
                  <li><a v-if="hasMobilepayPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and MobilePay payments') }} <i></i></a></li>
                  <li><a v-if="hasPaytrailPayments" href="javascript:;" v-on:click="refund(false)">{{ _('Refund transaction and Paytrail payments') }} <i></i></a></li>
                  <li><a href="javascript:;" v-on:click="refund(true)">{{ _('Refund transaction') }} <i></i></a></li>
                </Dropdown>
                <a class="btn btn-danger" v-else v-on:click="refund(true)"><i class="fa fa-undo fa-fw"></i> <span v-if="invoice.payment === 'cash'">{{ _('Refund receipt') }}</span><span v-else>{{ _('Refund transaction') }}</span></a>
              </template>
              <a class="btn btn-default" v-if="hasInvoice" v-on:click="displayInvoice"><i class="fa fa-file fa-fw"></i> {{ _('Display transaction') }}</a>
              <a class="btn btn-default" v-if="isDisplayConsolidatedVisible" v-on:click="displayConsolidatedOrder"><i class="fa fa-file fa-fw"></i> {{ _('Display consolidated order') }}</a>
              <a class="btn btn-warning" v-if="isSendOrderCancellationWithNotificationVisible" v-on:click="sendOrderCancellationWithNotification"><i class="fa fa-ban fa-fw"></i> {{ _('Send order cancellation message') }}</a>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <div class="form-group">
                  <label for="notes">{{ _('Notes') }}</label>
                  <textarea class="form-control" data-autoresize="true" rows="6" wrap="virtual" v-model="order.notes" id="notes"></textarea>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-4 order-infobox">
                <div class="alert alert-danger">
                  <strong>{{ _('Processing status') }}</strong>
                  <div class="radio">
                    <label>
                      <input type="radio" v-model="order.processed" name="processed" value="0" /> {{ _('Order has not been processed') }}
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input type="radio" v-model="order.processed" name="processed" value="1" /> {{ _('Order has been processed') }}
                    </label>
                  </div>
                  <div class="form-group" v-if="parseInt(processed)">
                    <label for="processdate">{{ _('Processed date') }}</label>
                    <input class="form-control" type="text" name="processdate" id="processDate" :value="processDate" disabled="disabled" />
                  </div>
                </div>
              </div>
              <div class="col-sm-4 order-infobox">
                <div class="alert alert-info">
                  <strong>{{ _('After sales') }}</strong>
                  <div class="radio">
                    <label>
                      <input type="radio" v-model="afterSalesSent" value="1" :disabled="!parseInt(order.marketing_allowed)" /> {{ _('Sent') }}
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input type="radio" v-model="afterSalesSent" value="0" :disabled="!parseInt(order.marketing_allowed)" /> {{ _('Not sent') }}
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input type="radio" v-model="afterSalesSent" value="2" :disabled="!parseInt(order.marketing_allowed)" /> {{ _('Won\'t be sent') }}
                    </label>
                  </div>
                  <span v-if="parseInt(order.marketing_allowed)">
                    <a v-if="parseInt(order.aftersales_sent) !== 1" class="btn btn-primary hidden-print" id="sendAftersalesButton" v-on:click="sendAftersalesMessage"><i class="fa fa-send fa-fw"></i> {{ _('Send now') }}</a>
                  </span>
                  <p v-else><strong>{{ _('Marketing NOT allowed') }}</strong></p>
                </div>
              </div>
            </div>
            <div class="row hidden-print" v-if="order.id && isFeatureEnabled('paymentplans')">
              <div class="col-md-12">
                <SubscriptionsPanel :order-id="order.id" :hide-empty="true" :details-trail="componentTrail" />
              </div>
            </div>
            <div class="row hidden-print">
              <div class="col-sm-4" v-if="!parseInt(processed)">
                <div id="processConfirmation" class="content_order_panel ui-state-default content_order_statuschange" v-if="parseInt(order.processed)">
                  <h4>{{ _('Do you want to send a process confirmation message to the customer\'s email address?') }}</h4>
                  <input type="radio" v-model="processconfirmation" value="1"> {{ _('Yes') }} <br />
                  <input type="radio" v-model="processconfirmation" value="0" checked> {{ _('No') }}<br />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('original')" class="tab-pane active" id="original" v-if="isTabActive('original')" key="original">
            <div class="row">
              <div class="col-md-12">
                <div id="originalOrders">
                  <h3>{{ _('Original orders') }}</h3>
                  <div class="table-responsive">
                    <table class="table table-striped" id="originalOrderTable">
                      <thead>
                        <tr>
                          <th>{{ _('Order no.') }}</th>
                          <th>{{ _('Customer name') }}</th>
                          <th>{{ _('Price vat 0%') }}</th>
                          <th>{{ _('Price inc. vat') }}</th>
                          <th>{{ _('Order date') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="oo in originalOrders" v-on:click="displayOrder(oo.id)">
                          <td class="is-clickable">{{ oo.id }}</td>
                          <td class="is-clickable"><span v-if="oo.billing_company">{{ oo.billing_company }}</span><span v-else-if="oo.company">{{ oo.company }}</span><span v-else>{{ oo.name }}</span></td>
                          <td class="is-clickable">{{ $filters.num(oo.totalWithDeliveryVatFree) }}</td>
                          <td class="is-clickable">{{ $filters.num(oo.totalWithDelivery) }}</td>
                          <td class="is-clickable">{{ $filters.timestampToDate(oo.orderdate) }} {{ $filters.timestampToTime(oo.orderdate) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('deliveries')" class="tab-pane active" id="deliveries" v-if="isFeatureEnabled('delivery2.0') && isTabActive('deliveries')" key="deliveries">
            <div class="row">
              <div class="col-md-12">
                <button class="btn btn-primary pull-right" v-on:click="expandAllDeliveries" v-if="isExpandAllAvailable"><i class="fa fa-fw fa-folder-open"></i> {{ _('Expand all') }}</button>
                <h3>{{ _('Deliveries') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div id="orderDeliveries" v-if="hasDeliveries">
                  <div class="table-responsive">
                    <table class="table">
                      <thead>
                        <tr>
                          <th style="border-left: 1px solid #ffffff">&nbsp;</th>
                          <th>{{ _('ID') }}</th>
                          <th>{{ _('Delivery method') }}</th>
                          <th v-if="isShipitEnabled">{{ _('Shipit status') }}</th>
                          <th>{{ _('Delivery date') }}</th>
                          <th>{{ _('Status') }}</th>
                          <th>{{ _('Pcs') }}</th>
                        </tr>
                      </thead>
                      <tbody style="border: 1px solid #dddddd">
                        <template v-for="(delivery, idx) in order.ownDelivery" :key="'delivery-' + delivery.id" >
                          <delivery-row-title :delivery="delivery" :order="order" :value="isDeliverySelected(delivery)" v-on:input="toggleDeliverySelection(delivery, $event)" v-on:toggle="toggleDelivery(delivery)"></delivery-row-title>
                          <delivery-row-details v-model="order.ownDelivery[idx]" :all-deliverymethods="allDeliverymethods" :all-tags="allDeliveryTags" :order="order" :all-selected-items="selectedItems" v-if="isDeliveryOpen(delivery)" v-on:delete="deleteDelivery(delivery)"></delivery-row-details>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div id="missingDeliveries" v-if="itemsWithoutDelivery.length">
                  <h3>{{ _('Products without a delivery') }}</h3>
                  <div class="table-responsive">
                    <table class="table table-borderless">
                      <thead>
                        <tr>
                          <th>&nbsp;</th>
                          <th>{{ _('ID') }}</th>
                          <th>{{ _('Product') }}</th>
                          <th>{{ _('Count') }}</th>
                          <th>{{ _('Price inc. vat') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <delivery-item-row v-for="(item, index) in itemsWithoutDelivery" :key="index" :item="item" :value="isItemRowSelected(item)" v-on:input="toggleItemRowSelection(item, $event)"></delivery-item-row>
                      </tbody>
                    </table>
                  </div>
                </div>
                <hr />
                <div class="flex-row">
                  <button class="btn btn-primary" v-if="isCombineAvailable" v-on:click="combineDeliveriesDialog"><i class="fa fa-fw fa-compress"></i> {{ _('Combine selected') }}</button>
                  <button class="btn btn-primary" v-else-if="isCreateDeliveryAvailable" v-on:click="createDeliveryDialog"><i class="fa fa-fw fa-plus"></i> {{ _('Create delivery') }}</button>
                  <button class="btn btn-success" v-if="isProcessMultipleAvailable" v-on:click="processMultipleDeliveries"><i class="fa fa-fw fa-cogs"></i> {{ _('Process') }}</button>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('messages')" class="tab-pane active" id="messages" v-if="isTabActive('messages')" key="messages">
            <div class="row">
              <div class="col-md-12">
                <div id="orderMessages">
                  <template v-if="(order.origin === 'channex-boo' || order.origin === 'channex-air') && isFeatureEnabled('channexmessaging')">
                    <button v-if="isChannexMessagingEnabled" class="btn btn-success pull-right" type="button" v-on:click="displayChannexMessageDialog()"><i class="fa fa-fw fa-envelope"></i> {{ _('Send message') }}</button>
                    <h3 v-if="order.origin === 'channex-boo'">{{ _('Messaging') }}: Booking.com</h3>
                    <h3 v-if="order.origin === 'channex-air'">{{ _('Messaging') }}: Airbnb</h3>
                    <br />
                    <div class="table-responsive" v-if="isChannexMessagingEnabled">
                      <table class="table table-striped">
                        <thead>
                          <tr>
                            <th>{{ _('Date') }}</th>
                            <th>{{ _('Sender') }}</th>
                            <th>{{ _('Message') }}</th>
                          </tr>
                        </thead>
                        <tbody v-if="channexMessages && channexMessages.length">
                          <tr v-for="message in channexMessages" v-on:click="displayChannexMessageDialog(message)" style="cursor: pointer">
                            <td class="is-clickable">{{ DateTime.fromISO(message.inserted_at).toFormat(dateFormat + ' ' + timeFormat) }} <i v-if="!parseInt(message.seen)" class="fa fa-fw fa-envelope-dot"></i></td>
                            <td class="is-clickable" v-if="message.sender === 'guest'"><i class="fa fa-fw fa-male"></i> {{ order.name }}</td>
                            <td class="is-clickable" v-else><i class="fa fa-fw fa-cash-register"></i> <i>{{ _('Merchant') }}</i></td>
                            <td class="is-clickable" :style="(parseInt(message.seen) ? '' : 'font-weight: bold')"><a v-if="message.sender === 'guest'" v-text="formatChannexMessage(message.message)" style="white-space: pre-line"></a><span v-else v-text="formatChannexMessage(message.message)" style="white-space: pre-line"></span></td>
                          </tr>
                        </tbody>
                        <tbody v-else>
                          <tr>
                            <td colspan="5">{{ _('No messages found.') }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <button v-else v-on:click="enableMessaging()" class="btn btn-info" type="button"><i class="fa fa-fw fa-message-plus"></i> {{ _('Enable messaging') }}</button>
                  </template>
                  <h3>{{ _('Johku Messaging') }}</h3>
                  <div class="table-responsive">
                    <table class="table table-striped" id="messageTable">
                      <thead>
                        <tr>
                          <th>{{ _('Date') }}</th>
                          <th>{{ _('Message subject') }}</th>
                          <th>{{ _('Recipient') }}</th>
                          <th>{{ _('Message') }}</th>
                          <th>{{ _('Status') }}</th>
                        </tr>
                      </thead>
                      <tbody v-if="order.messages && order.messages.length">
                        <tr v-for="message in order.messages" v-on:click="displayMessage(message)">
                          <td class="is-clickable">{{ $filters.timestampToDate(message.creationdate) }} {{ $filters.timestampToTime(message.creationdate) }}</td>
                          <td class="is-clickable"><a><strong>{{ message.subject }}</strong></a></td>
                          <td class="is-clickable">{{ message.to }}</td>
                          <td class="is-clickable">{{ message.summary }}</td>
                          <td class="is-clickable"><span v-if="message.latestEvent">{{ getEmailEventType(message.latestEvent.type) }}</span><span v-else>{{ _('Sent') }}</span></td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr>
                          <td colspan="5">{{ _('No messages found.') }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="parseInt(order.skip_notifications)" class="well">
                    {{ _('Notifications:') }} {{ _('Skipped') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('notifications')" class="tab-pane active" id="notifications" v-if="isTabActive('notifications')" key="notifications">
            <div class="row">
              <div class="col-md-12">
                <h3>{{ _('Notification schedule') }}</h3>
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th>{{ _('Send time') }}</th>
                        <th>{{ _('Notification') }}</th>
                        <th>{{ _('Recipient') }}</th>
                        <th>{{ _('Status') }}</th>
                      </tr>
                    </thead>
                    <tbody v-if="notificationEvents.length">
                      <tr v-for="event in notificationEvents" :key="event.id" v-on:click="displayNotificationEvent(event)" style="cursor: pointer">
                        <td class="is-clickable">{{ $filters.timestampToDate(event.ts) }} {{ $filters.timestampToTime(event.ts) }}</td>
                        <td class="is-clickable">{{ event.notification }}</td>
                        <td class="is-clickable">{{ notificationEventRecipient(event) }}</td>
                        <td class="is-clickable">{{ notificationEventStatus(event.status) }}</td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr>
                        <td colspan="4">{{ _('No results found.') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('requests')" class="tab-pane active" id="requests" v-if="isTabActive('requests')" key="requests">
            <div class="row">
              <div class="col-md-12">
                <h3>{{ _('Customer requests') }}</h3>
                <div v-if="orderRequests.length">
                  <div class="panel panel-default" v-for="request in orderRequests" :key="request.id" style="margin-bottom: 15px;">
                    <div class="panel-heading">
                      <strong>{{ formatRequestType(request.type) }}</strong>
                      <span class="label" :class="requestStatusClass(request.status)" style="margin-left: 10px;">{{ formatRequestStatus(request.status) }}</span>
                      <span class="text-muted pull-right">{{ formatRequestTimestamp(request.created_ts) }}</span>
                    </div>
                    <div class="panel-body">
                      <p v-if="request.details"><strong>{{ _('Additional details') }}:</strong><br /><span style="white-space: pre-line">{{ request.details }}</span></p>
                      <div v-if="parseRequestItems(request).length">
                        <div v-for="(item, index) in parseRequestItems(request)" :key="index" style="margin-bottom: 10px;">
                          <strong>{{ item.product_name || (_('Item') + ' #' + item.item_id) }}</strong>
                          <div v-if="item.current_start_ts">{{ _('Previous time') }}: {{ formatRequestTimestampRange(item.current_start_ts, item.current_end_ts) }}</div>
                          <div v-if="item.requested_start_ts">{{ request.status === 'processed' ? _('New time') : _('Requested new time') }}: {{ formatRequestTimestampRange(item.requested_start_ts, item.requested_end_ts) }}</div>
                        </div>
                      </div>
                      <div v-if="request.status !== 'pending' && getRequestNotes(request)">
                        <strong>{{ _('Notes') }}</strong>
                        <p style="white-space: pre-line">{{ getRequestNotes(request) }}</p>
                      </div>
                      <div v-if="request.status === 'pending'">
                        <div class="form-group">
                          <label :for="'requestNotes' + request.id">{{ _('Notes') }}</label>
                          <textarea :id="'requestNotes' + request.id" class="form-control" rows="3" v-model="requestNotesDrafts[request.id]"></textarea>
                        </div>
                        <button class="btn btn-success" type="button" v-on:click="processOrderRequest(request, 'processed')"><i class="fa fa-fw fa-check"></i> {{ _('Mark as processed') }}</button>
                        <button class="btn btn-danger" type="button" style="margin-left: 8px;" v-on:click="processOrderRequest(request, 'denied')"><i class="fa fa-fw fa-ban"></i> {{ _('Mark as denied') }}</button>
                      </div>
                      <p v-else-if="request.processed_ts" class="text-muted">{{ _('Handled') }}: {{ formatRequestTimestamp(request.processed_ts) }}</p>
                    </div>
                  </div>
                </div>
                <p v-else class="text-muted">{{ _('No requests found.') }}</p>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('conversation')" class="tab-pane active" id="conversation" v-if="hubOrder && isTabActive('conversation')" key="conversation">
            <HubOrderPane :shopId="shopId" :order="hubOrder" class="order-conversation" />
          </div>
          <div v-bind="tabPanel('terms')" class="tab-pane active" id="terms" v-if="isTabActive('terms')" key="terms">
            <div class="row">
              <div class="col-md-12">
                <div id="orderTerms">
                  <h3>{{ _('Accepted terms') }}</h3>
                  <div class="table-responsive">
                    <table class="table table-striped" id="messageTable">
                      <thead>
                        <tr>
                          <th>{{ _('Date') }}</th>
                          <th>{{ _('Type') }}</th>
                          <th>{{ _('Name') }}</th>
                        </tr>
                      </thead>
                      <tbody v-if="order.ownAcceptedterms && order.ownAcceptedterms.length">
                        <tr v-for="at in order.ownAcceptedterms" v-on:click="displayTerms(at)">
                          <td class="is-clickable">{{ $filters.timestampToDate(at.ts) }} {{ $filters.timestampToTime(at.ts) }}</td>
                          <td class="is-clickable">{{ formatTermsType(at.type) }}</td>
                          <td class="is-clickable"><a><strong>{{ at.name }}</strong></a></td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr>
                          <td colspan="3">{{ _('No accepted terms found.') }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="button-col" v-if="showRemoveButton || showArchiveButton">
          <template v-if="isRemoveButtonEnabled">
            <a class="btn btn-danger" id="removeButton" v-on:click="remove()"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{_('Delete order') }}</span></a>
          </template>
          <template v-if="showArchiveButton">
            <a v-if="!parseInt(order.archived)" class="btn btn-warning" v-on:click="archiveDialog"><i class="fa fa-fw fa-ban"></i><span class="hidden-xs"> {{ _('verb|Archive') }}</span></a>
            <a v-else class="btn btn-warning" v-on:click="restore"><i class="fa fa-fw fa-globe"></i><span class="hidden-xs"> {{ _('Restore from archive') }}</span></a>
          </template>
        </div>
        <div class="button-col">
          <a class="btn btn-success" id="saveButton" v-if="showSaveButton" v-on:click="save(false)" :disabled="isSaveButtonEnabled ? undefined : true"><i class="fa fa-fw fa-save"></i><span> {{ _('Save') }}</span></a>
          <Dropdown buttonClass="btn-info" direction="up" v-if="showInvoiceButtons">
            <template #trigger>
              <i class="fa fa-fw fa-check"></i> {{ _('Approve') }} &nbsp;<span class="caret"></span>
            </template>
            <li><a href="javascript:;" class="invoiceCompleteButton" v-on:click="confirmDialog('admincard')">{{ _('Card') }} <i></i></a></li>
            <li><a href="javascript:;" class="invoiceCompleteButton" v-on:click="confirmDialog('admincash')">{{ _('Cash') }} <i></i></a></li>
            <li><a href="javascript:;" class="invoiceCompleteButton" v-on:click="confirmDialog('bill')">{{ _('Invoice') }} <i></i></a></li>
            <li><a href="javascript:;" class="invoiceCompleteButton" v-on:click="confirmDialog('none')">{{ _('Transaction without payments') }} <i></i></a></li>
            <li v-if="order.payment === 'open'" role="separator" class="divider"></li>
            <li v-if="order.payment === 'open'"><a href="javascript:;" class="invoiceCompleteButton" v-on:click="confirmDialog('confirm')">{{ _('Confirm order') }} <i></i></a></li>
            <li><a href="javascript:;" class="invoiceCompleteButton" v-on:click="confirmDialog('adminnone')">{{ _('No charge') }} <i></i></a></li>
          </Dropdown>
          <a class="btn btn-default" id="cancelButton" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></a>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import { storefrontOrigin } from '~/lib/storefrontOrigin'
import axios from 'axios'
import { DateTime } from 'luxon'
import throttle from 'lodash/throttle'
import paymentTypes from '~/lib/paymenttypes'
import changewatcher from '~/lib/changewatcher'
import channexhelpers from '~/lib/channexhelpers'
import components from '~/lib/components'
import settingshelpers from '~/lib/settingshelpers'
import { isAnonymized } from '~/lib/anonymization'
import { hubState } from '~/lib/hubState'
// Johku Hub's code only when an order's conversation shows
const HubOrderPane = defineAsyncComponent(() => import('~/components/HubOrderPane'))
import { DeliveryItemRow, DeliveryRowDetails, DeliveryRowTitle, ProductItem, ReservationItem, ModalResourceDetails as ResourceDetails, ModalReservationDetails as ReservationDetails, PaymentLink, ConfirmationLink, ValuecardDetails, SentMessageDetails, InvoiceDetails, PaymentPlanSelector, ANotificationDetails, SubscriptionsPanel, CustomerSelector } from '#components'
import { mapGetters } from 'vuex'
import nl2br from 'nl2br'

export default {
  data () {
    return {
      order: {},
      // originalState: '',
      deleted: false,
      invoice: null,
      allTags: [],
      allDeliveryTags: [],
      tagToAdd: '',
      originalOrders: [],
      allDeliverymethods: [],
      messages: [],
      customerDetailsEditable: false,
      billingCustomerDetailsEditable: false,
      skipNotifications: false,
      processconfirmation: '0',
      processed: '0',
      openDeliveries: [],
      selectedDeliveries: [],
      selectedItems: [],
      selectedFolioItems: [],
      folioSelectMode: false,
      channexMessages: [],
      notificationEvents: [],
      participantName: '',
      isParticipantDialogVisible: false,
      editableParticipantName: '',
      packageExpanded: [],
      DateTime: DateTime,
      selectedPaymentPlan: null,
      requestNotesDrafts: {},
      refunding: false
    }
  },
  mixins: [changewatcher, channexhelpers, components, paymentTypes, settingshelpers],
  computed: {
    ...mapGetters(['countries', 'floatingReceipt']),
    primaryCountry () {
      return { id: 'FI', country: this.countries['FI'] }
    },
    priorityCountries () {
      return ['SE', 'NO', 'DK', 'EE', 'RU'].reduce((obj, code) => {
        if (this.countries[code]) {
          obj[code] = this.countries[code]
        }
        return obj;
      }, {})
    },
    baseIdentifier () {
      return this.order && this.order.identifier ? this.order.identifier.split('@')[0] : ''
    },
    isMainOrder () {
      return this.order && this.order.identifier === this.baseIdentifier
    },
    isAdvanceOrder () {
      return this.order && this.order.identifier === this.baseIdentifier + '@1'
    },
    isFinalOrder () {
      return this.order && this.order.identifier === this.baseIdentifier + '@2'
    },
    selectedPlanOrderTab () {
      return this.isMainOrder ? 'main' : this.isAdvanceOrder ? 'advance' : this.isFinalOrder ? 'final' : null
    },
    planOrderTabs () {
      return [
        { id: 'main', label: this._('Summary'), icon: 'fa-shopping-cart' },
        { id: 'advance', label: this._('Advance payment'), icon: 'fa-credit-card', ...this.paidBadge(this.advanceOrderStatus) },
        { id: 'final', label: this._('Final payment'), icon: 'fa-calendar-check', ...this.paidBadge(this.finalOrderStatus) }
      ]
    },
    folioOrderTabs () {
      return this.folioOrders.map(folio => ({ id: String(parseInt(folio.id)), label: this._('Order') + ' ' + folio.id, icon: 'fa-shopping-cart', ...this.paidBadge({ paid: parseInt(folio.paid) }) }))
    },
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'original', label: this._('Original orders'), hidden: !(this.originalOrders && this.originalOrders.length) },
        { id: 'deliveries', label: this._('Deliveries'), badge: this.deliveryIds.length, hidden: !this.isFeatureEnabled('delivery2.0') },
        { id: 'messages', label: this._('Messages'), badge: this.messageCount > 0 ? this.messageCount : null },
        { id: 'conversation', label: this._('Conversation'), hidden: !this.hubOrder, badge: this.hubOrder && hubState.unreadOrders.includes(String(this.order.id)) ? 1 : null },
        { id: 'notifications', label: this._('Notification schedule'), badge: this.notificationEvents.length },
        { id: 'requests', label: this._('Requests'), badge: this.pendingRequestCount > 0 ? this.pendingRequestCount : null, badgeClass: 'btn-warning' },
        { id: 'terms', label: this._('Terms'), badge: this.termsCount > 0 ? this.termsCount : null }
      ])
    },
    isAnonymizedRecord () {
      return isAnonymized(this.order)
    },
    // The order's conversation with the customer in Johku Hub, for users who have the shop's messages
    // (behind the shop's hubmessaging feature flag)
    hubOrder () {
      if (!this.isFeatureEnabled('hubmessaging') || !hubState.available || !this.order || !this.order.identifier || this.isAnonymizedRecord) {
        return null
      }
      return { identifier: this.order.identifier, locale: this.order.version || this.version, orderUrl: this.orderInfoUrl }
    },
    anonymizedLockStatus () {
      return this._('This record has been anonymized and cannot be modified.')
    },
    isOrderLocked () {
      return this.locked || (this.order && this.order.payment === 'open' && this.hasLockedItems)
    },
    orderLockStatus () {
      if (this.hasLockedItems) {
        return this._('The payment link has already been activated. Unlock the order for modification?')
      }
      return this.lockStatus
    },
    // Orders booked through an external activity channel (Bókun, GetYourGuide) are managed in that channel
    isChannelOrder () {
      return !!(this.order && this.order.origin && (this.order.origin.startsWith('bokun') || this.isGygOrder))
    },
    isGygOrder () {
      return !!(this.order && this.order.origin === 'gyg')
    },
    isResendBarVisible () {
      return this.order && !this.isRefund && !this.isRefunded && ((this.hasValueCards && this.hasInvoice) || this.hasTickets)
    },
    isSaveButtonEnabled () {
      return !this.saving && !this.saved && !this.loading && !this.isOrderLocked && !this.isAnonymizedRecord
    },
    isApproveButtonEnabled () {
      return this.isSaveButtonEnabled && this.order && !this.isChannelOrder
    },
    isRemoveButtonEnabled () {
      return !this.saving && !this.saved && !this.loading && !this.isOrderLocked && this.order && !this.isChannelOrder && !this.invoice
    },
    isArchiveButtonEnabled () {
      return this.isSaveButtonEnabled && this.order && !this.isChannelOrder
    },
    hasValueCards () {
      return !!this.allItems.find(i => i.ownValuecard && i.ownValuecard.length)
    },
    hasActiveValueCards () {
      return this.allItems.find(i => i.ownValuecard && i.ownValuecard.some(v => parseInt(v.status)))
    },
    hasTickets () { // Check that we have a) tickets and b) reservations to shows that have not already ended or c) we have ticket activities
      return !!(
        (this.allItems.find(i => i.show_id && i.item_id) && !!this.allItems.find(i => i.show_id && i.end_ts && parseInt(i.end_ts, 10) > DateTime.now({ zone: this.timezone }).toUnixInteger())) || 
        (this.allItems.find(i => i.product_subtype == this.SUBTYPE_TICKET && !i.show_id))
      )
    },
    hasPackagedOptions () {
      return !!this.allItems.find(i => !!i.packages)
    },
    isChannexMessagingEnabled () {
      if (this.settings) {
        const settings = this.settings
        if (settings && settings.channex_group && parseInt(settings.channex_messaging)) {
          return true
        }
      }
      return false
    },
    formattedOrigin () {
      if (!this.order) {
        return ''
      }
      if (this.isGygOrder && this.order.reference) {
        return this.formatOrigin(this.order.origin) + ' · ' + this.order.reference
      }
      return this.formatOrigin(this.order.origin)
    },
    messageCount () {
      let total = 0
      if (this.order) {
        if (this.order.messages && this.order.messages.length) {
          total += this.order.messages.length
        }
        if (this.channexMessages && this.channexMessages.length) {
          total += this.channexMessages.length
        }
      }
      return total
    },
    termsCount () {
      if (this.order && this.order.ownAcceptedterms && this.order.ownAcceptedterms.length) {
        return this.order.ownAcceptedterms.length
      }
      return 0
    },
    orderRequests () {
      if (!this.order) {
        return []
      }
      const requests = this.order.orderrequests || this.order.ownOrderrequest
      return requests && requests.length ? requests : []
    },
    pendingRequestCount () {
      return this.orderRequests.filter((request) => request.status === 'pending').length
    },
    storefrontversions () {
      if (!this.settings || !this.settings.storefrontversions) {
        return []
      }
      return this.settings.storefrontversions.split(';')
    },
    allversions () {
      return [...new Set([
        ...(this.settings.versions ? this.settings.versions.split(';') : []),
        ...this.storefrontversions
      ])]
    },
    isBroken () {
      return !this.isAnonymizedRecord && !this.loading && this.order && !this.invoice && !this.order.paymentplan && this.order.payment !== 'adminnone' && this.order.payment !== 'open' && !parseInt(this.order.confirmed) && parseInt(this.order.orderdate) < DateTime.now({ zone: this.timezone }).toUnixInteger() - 1800 // Added a 30 minute grace period for finishing the order before marking the order as broken
    },
    showConvertToOpenOrder () {
      return !this.isAnonymizedRecord && this.order && parseInt(this.order.confirmed) === 1 && !this.isChannelOrder
    },
    hasDeliveries () {
      return this.order && this.order.ownDelivery && this.order.ownDelivery.length
    },
    isExpandAllAvailable () {
      if (this.hasDeliveries) {
        let countOpen = 0
        Object.keys(this.openDeliveries).forEach(key => {
          if (this.openDeliveries[key]) {
            countOpen++
          }
        })
        if (countOpen < this.order.ownDelivery.length) {
          return true
        }
      }
      return false
    },
    itemsWithoutDelivery () {
      if (!this.order || !this.order.ownItem || !this.order.ownItem.length) {
        return []
      }
      return this.order.ownItem.filter(i => !i.delivery_id && !parseInt(i.isdelivery) && i.product_name !== this._('Surcharge'))
    },
    isCombineAvailable () {
      return this.selectedDeliveries.length && this.selectedDeliveries.length + this.selectedItems.length > 1
    },
    isCreateDeliveryAvailable () {
      return !!this.selectedItems.length
    },
    selectedNotProcessedDeliveries () {
      return this.order && this.order.ownDelivery && this.selectedDeliveries ? this.order.ownDelivery.filter(od => this.selectedDeliveries.includes(od.id) && parseInt(od.id) && !parseInt(od.processed)).map(d => d.id) : []
    },
    isProcessMultipleAvailable () {
      if (this.selectedNotProcessedDeliveries.length && !this.selectedItems.length) {
        return true
      }
      return false
    },
    reservationIds() {
      let ids = []
      if (this.reservationItems && this.reservationItems.length) {
        this.reservationItems.forEach(item => {
          if (item && item.ownReservation && item.ownReservation.length) {
            ids = [...ids, ...item.ownReservation.map(r => parseInt(r.id, 10))]
          }
        })
      }
      return ids
    },
    deliveryIds() {
      if (!this.order || !this.order.ownDelivery) {
        return []
      }
      return this.order.ownDelivery.map(d => parseInt(d.id, 10))
    },
    isModifyOrderEnabled () {
      return !this.isOrderLocked && !this.isAnonymizedRecord
    },
    isModifyOrderAvailable () {
      return this.floatingReceipt && this.order && !this.order.subscription_renewal && this.order.payment === 'open' && (!this.order.paymentplan || this.order.id !== this.mainOrderId)
    },
    folioOrders () {
      const folio = this.order && this.order.folio
      if (folio && folio.items) {
        return folio.items
      }
      return Array.isArray(folio) ? folio : []
    },
    folioSelectableItems () {
      return this.topLevelItems.filter(item => !item.bill_id && !parseInt(item.locked) && !item.valuecard_id && !item.voucher_id && !item.discount_id && !item.item_id && !item.modifier_id && !parseInt(item.isdelivery))
    },
    folioLeavesItemBehind () {
      // Mirrors the backend: discount and delivery rows can't move, so they don't count as items left on the order
      const selected = {}
      this.selectedFolioItems.forEach(id => { selected[parseInt(id, 10)] = true })
      return this.allItems.some(item => {
        const parentId = parseInt(item.item_id, 10)
        const modifierId = parseInt(item.modifier_id, 10)
        const moves = selected[parseInt(item.id, 10)] || (parentId && selected[parentId]) || (modifierId && selected[modifierId])
        return !moves && !item.discount_id && !parseInt(item.isdelivery)
      })
    },
    selectedFolioTotal () {
      return this.folioSelectableItems.filter(item => this.isFolioItemSelected(item)).reduce((sum, item) => sum + this.folioItemTotal(item), 0)
    },
    isFolioMoveAvailable () {
      if (!this.isFeatureEnabled('folio') || !this.order || !parseInt(this.order.folio_eligible) || this.order.payment !== 'open') {
        return false
      }
      return this.folioSelectableItems.length > 1
    },
    isModifyConfirmedOrderAvailable () {
      return this.floatingReceipt && this.order && !this.order.subscription_renewal && parseInt(this.order.confirmed) === this.ORDER_CONFIRMED && !this.isChannelOrder
    },
    allItems() {
      return this.order && this.order.ownItem ? this.order.ownItem : []
    },
    topLevelItems() {
      return this.allItems.filter(i => !i.item_id || (i.item_id && i.optiontype && parseInt(i.optiontype) === this.LINKEDPRODUCT_OFFER))
    },
    productItems () {
      return this.order && this.order.ownItem ? this.order.ownItem.filter(i => !i.reservation_datestart) : []
    },
    reservationItems () {
      return this.order && this.order.ownItem ? this.order.ownItem.filter(i => !!i.reservation_datestart) : []
    },
    orderInfoUrl () {
      if (!this.settings || !this.order || !this.order.identifier) {
        return ''
      }
      if (this.isAnonymizedRecord) {
        return ''
      }
      if (parseInt(this.settings.legacystorefront)) {
        return ''
      }
      let url = storefrontOrigin(this.shopId, this.settings.shopdomain, useRuntimeConfig().public.storefrontDomain) + '/'
      url += this.version + '/orderinfo/' + this.order.identifier
      return url
    },
    deliveryName () {
      if (!this.order || !parseInt(this.order.delivery)) {
        return ''
      }
      if (this.shopDeliveryMethods[this.shopId]) {
        const dm = this.shopDeliveryMethods[this.shopId].find(d => parseInt(d.id, 10) === parseInt(this.order.delivery, 10))
        if (dm) {
          return dm.name
        }
      }
      return ''
    },
    orderStatusClass () {
      let ret = ''
      if (!this.order) {
        return ret
      }
      if (!parseInt(this.order.paid) && this.order.payment !== 'adminnone' && this.order.payment !== 'open' && parseInt(this.order.confirmed) !== 1 && parseInt(this.order.confirmed) !== 3) {
        ret = 'bg-danger'
      }
      if (this.order.payment === 'open' && parseInt(this.order.confirmed) !== 1) {
        ret = 'bg-info'
      }
      if (parseInt(this.order.confirmed) === 1) {
        ret = 'bg-warning'
      }
      return ret
    },
    orderStatus () { // This is copy-paste from formatters.js... maybe these could be unified at some point, but it's Friday again
      if (!this.order) {
        return ''
      }
      const _ = this._
      let ret = _('Unknown')
      switch (parseInt(this.order.paid)) {
        case 0:
          ret = _('Unpaid')
          if (parseInt(this.order.groupdeal)) { // group deal
            ret += '&nbsp;(' + _('Group') + ')'
          }
          if (parseInt(this.order.confirmed) === 1) {
            ret = _('Confirmed')
          } else if(parseInt(this.order.confirmed) === 2) {
            ret = _('Unpaid')
          } else if(parseInt(this.order.confirmed) === 3) {
            ret = _('Billed')
          } else if(!parseInt(this.order.completed)) {
            const ignore = ['bill', 'open', 'transfer', 'cash', 'admincash', 'admincard', 'adminnone', 'multi', 'voucher', 'none']
            if (this.order.payment && !ignore.includes(this.order.payment)) {
              ret = _('Transaction incomplete')
            }
          }
          break
        case 1:
          ret = _('Paid')
          break
        case 2:
          ret = _('No charge')
          break
      }
      if (parseInt(this.order.refunded)) { // refunded
        ret = _('Refunded')
      }
      if (this.order.payment === 'adminnone') {
        ret = _('No charge')
      }
      return ret
    },
    paymentName () {
      return this.order ? this.getPaymentTitle(this.order.payment) : ''
    },
    bamboraId () {
      return this.order ? this.order.bamboraid : null
    },
    processDate () {
      return this.order && this.order.processdate ? DateTime.fromSeconds(Number(this.order.processdate), { zone: this.timezone }).toFormat(this.dateFormat) : ''
    },
    payments () {
      return this.invoice && this.invoice.ownPayment ? this.invoice.ownPayment.filter(p => !(p.method === 'voucher' && parseFloat(p.amount) === 0)) : []
    },
    hasVismaPayPayments () {
      return this.payments ? this.payments.filter(p => p.method === 'paybywaychannel' || p.method === 'bambora' || (p.method === 'admincard' && parseInt(p.bambora_id))).length > 0 : false
    },
    hasVivaPayments () {
      return this.payments ? this.payments.filter(p => p.method === 'viva').length > 0 : false
    },
    hasMobilepayPayments () {
      return this.payments ? this.payments.filter(p => p.method === 'mobilepay' && p.mobilepay_reference).length > 0 : false
    },
    hasPaytrailPayments () {
      // Same conditions as the backend's Paytrail refund: checkout payment with a stored transaction id (-1 marks a failed payment)
      const payment = (this.order && this.order.payment) || (this.invoice && this.invoice.payment)
      const transactionId = (this.order && this.order.transactionid) || (this.invoice && this.invoice.transactionid)
      return payment === 'checkout' && !!transactionId && String(transactionId) !== '-1'
    },
    showArchiveButton () {
      if (!this.order) {
        return false
      }
      if (!this.order.paid && (this.order.payment === 'creditcard' || this.order.payment === 'paypal' || this.order.payment === 'verkkomaksut' || this.order.payment === 'checkout')) {
        return false
      }
      return true
    },
    showRemoveButton () {
      if (!this.order) {
        return false
      }
      if (!this.order.paid && (this.order.payment === 'creditcard' || this.order.payment === 'paypal' || this.order.payment === 'verkkomaksut' || this.order.payment === 'checkout')) {
        return false
      }
      return true
    },
    showSaveButton () {
      if (!this.order) {
        return false
      }
      if (!this.order.paid && (this.order.payment === 'creditcard' || this.order.payment === 'paypal' || this.order.payment === 'verkkomaksut' || this.order.payment === 'checkout')) {
        return false
      }
      return true
    },
    showInvoiceButtons () {
      if (!this.order) {
        return false
      }
      if (this.invoice) {
        return false
      }
      if (this.order.payment !== 'open' && parseInt(this.order.confirmed, 10) !== this.ORDER_CONFIRMED) {
        return false
      }
      return true
    },
    isPaymentLinkVisible () {
      return !this.invoice && this.order && !this.isAnonymizedRecord && this.order.payment !== 'adminnone' && !parseInt(this.order.consolidate_id) && !this.isChannelOrder
    },
    showConfirmationLink () {
      return this.isPaymentLinkVisible && this.order.payment === 'open'
    },
    isPrintReceiptVisible () {
      return this.order && parseInt(this.order.paid) && this.order.payment !== 'adminnone'
    },
    isPrintInvoiceVisible () {
      if (!this.order || !this.order.identifier) {
        return false
      }
      if (this.isNocfoAccountingEnabled) {
        return !!(this.invoice && this.invoice.nocfo_invoice_id)
      }
      if (this.isNetvisorAccountingEnabled) {
        return !!(this.invoice && parseInt(this.invoice.netvisor))
      }
      if (this.isBrioxAccountingEnabled) {
        return !!(this.invoice && this.invoice.briox_invoice_id)
      }
      return this.order.payment === 'bill'
    },
    isPrintTransferVisible () {
      return this.order && this.order.identifier && (this.order.payment === 'transfer' || this.order.payment === 'cash') && this.order.totalWithDelivery != 0
    },
    isPrintDeliveryManifestVisible () {
      return true
    },
    isRefund () {
      return this.order && parseInt(this.order.isrefund)
    },
    isRefunded () {
      return this.order && parseInt(this.order.refunded)
    },
    isRefundVisible () {
      return this.order && this.invoice && this.order.payment !== 'adminnone' && this.order.payment !== 'open' && parseInt(this.order.confirmed, 10) !== this.ORDER_CONSOLIDATED && parseInt(this.order.confirmed, 10) !== this.ORDER_CONFIRMED && !this.isRefunded && !this.refunding
    },
    isDisplayConsolidatedVisible () {
      return this.order && parseInt(this.order.consolidate_id)
    },
    isSendOrderCancellationWithNotificationVisible () {
      return this.order && !this.isAnonymizedRecord && !this.isChannelOrder
    },
    hasInvoice () {
      return this.invoice && this.invoice.id
    },
    hasBillingCustomer () {
      return this.order && (this.order.billing_name || this.order.billing_company)
    },
    tags () {
      return this.order && this.order.sharedTag ? this.order.sharedTag.filter(t => t.title) : []
    },
    allSelectableTags () {
      const ids = this.tags.map(t => t.id)
      return this.allTags.filter(t => !ids.includes(t.id))
    },
    afterSalesSent: {
      get () {
        if (!parseInt(this.order.aftersales_sent)) {
          return '0'
        }
        return this.order.aftersales_sent + ''
      },
      set (newValue) {
        this.order.aftersales_sent = newValue
      }
    },
    hasLockedItems () {
      if (!this.order || !this.order.ownItem || !this.order.ownItem.length) {
        return false
      }
      return this.order.ownItem.filter(i => i.locked && parseInt(i.locked)).length > 0
    },
    verifiedCustomer () {
      if (this.order && this.order.verifiedcustomer) {
        return { ts: this.order.verifiedcustomer.ts, ...JSON.parse(this.order.verifiedcustomer.data) }
      }
      return null
    },
    hasPaymentPlan () {
      return this.order && this.order.paymentplan && this.paymentPlan
    },
    paymentPlan: {
      get () {
        return this.order && this.order.paymentplan ? JSON.parse(this.order.paymentplan) : null
      },
      set (newValue) {
        Vue.set(this.order, 'paymentplan', newValue ? JSON.stringify(newValue) : null)
      }
    },
    paymentPlanAmount: {
      get () {
        return this.paymentPlan ? parseFloat(this.paymentPlan.amount) || 0 : 0
      },
      set (newValue) {
        this.paymentPlan = { ...this.paymentPlan, amount: parseFloat(newValue) || 0 }
      }
    },
    paymentPlanDate: {
      get () {
        return this.paymentPlan && this.paymentPlan.final_payment_date
          ? DateTime.fromISO(this.paymentPlan.final_payment_date).toJSDate()
          : null
      },
      set (newValue) {
        this.paymentPlan = {
          ...this.paymentPlan,
          final_payment_date: newValue ? DateTime.fromJSDate(newValue).toISODate() : null
        }
      }
    },
    mainOrderId () {
      return this.paymentPlan && this.paymentPlan.order_ids && this.paymentPlan.order_ids.length > 0 ? this.paymentPlan.order_ids[0] : null
    },
    advanceOrderId () {
      return this.paymentPlan && this.paymentPlan.order_ids && this.paymentPlan.order_ids.length > 0 ? this.paymentPlan.order_ids[1] : null
    },
    finalOrderId () {
      return this.paymentPlan && this.paymentPlan.order_ids && this.paymentPlan.order_ids.length > 0 ? this.paymentPlan.order_ids[2] : null
    },
    advanceOrderStatus () {
      return this.paymentPlan && this.paymentPlan.order_status
        ? this.paymentPlan.order_status.find(o => parseInt(o.id) === parseInt(this.advanceOrderId))
        : null
    },
    finalOrderStatus () {
      return this.paymentPlan && this.paymentPlan.order_status
        ? this.paymentPlan.order_status.find(o => parseInt(o.id) === parseInt(this.finalOrderId))
        : null
    }
  },
  watch: {
    locked (newValue) {
      if (newValue && this.order && this.order.id) {
        this.EventBus.$emit('closeOrderEditor', {id: this.order.id})
      }
    }
  },
  methods: {
    selectPaymentPlanDialog () {
      this.showModal('paymentplanselector', PaymentPlanSelector, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          orderItems: this.allItems,
          currency: this.settings.currency,
          dateFormat: this.dateFormat
        },
        events: {
          confirm: this.selectPaymentPlan
        },
        width: 900
      })
    },
    async selectPaymentPlan ({ paymentPlanId, itemIds }) {
      this.selectedPaymentPlan = { planId: paymentPlanId, itemIds }
      try {
        await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, { id: this.order.id, paymentplan_id: paymentPlanId, item_ids: itemIds })
      } catch (e) {
        this.showError(e)
        return
      }
    },
    openMainOrder () {
      this.component.initData.orderId = this.mainOrderId
      this.loadOrder(this.mainOrderId)
    },
    openAdvanceOrder () {
      this.component.initData.orderId = this.advanceOrderId
      this.loadOrder(this.advanceOrderId)
    },
    openFinalOrder () {
      this.component.initData.orderId = this.finalOrderId
      this.loadOrder(this.finalOrderId)
    },
    openPlanOrder (tab) {
      if (tab === 'main') {
        this.openMainOrder()
      } else if (tab === 'advance') {
        this.openAdvanceOrder()
      } else if (tab === 'final') {
        this.openFinalOrder()
      }
    },
    expandPackage (item) {
      this.packageExpanded[item.id] = true
    },
    isOptionExpanded (item, option) {
      if (!option.packages) {
        return true
      }
      return this.packageExpanded[item.id] ? true : false
    },
    getPackageOptionPrice (item) {
      const children = this.getItemOptions(item).filter(i => !!i.packages)
      let total = 0
      children.forEach(child => {
        total += Number(child.price) * Number(child.count) * Math.max(Number(child.resource_count), 1)
      })
      return total
    },
    removeParticipantDialog (parentItem, option) {
      this.showDialog({
        title: this._('Are you sure? Selected reservations will be deleted permanently.'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Remove'),
            class: 'danger',
            icon: 'trash',
            handler: this.removeParticipant(parentItem, option)
          }
        ],
      })
    },
    removeParticipant (parentItem, option) {
      return async (data) => {
        const itemRef = JSON.parse(option.packages).itemRef
        const pArr = itemRef.split('-')
        const participantIndex = Number(pArr[0])
        const children = this.getItemOptions(parentItem)
        const removeItems = []
        children.forEach(c => {
          if (c.packages) {
            const packages = JSON.parse(c.packages)
            const itemRef = packages.itemRef
            const pArr = itemRef.split('-')
            if (participantIndex === Number(pArr[0])) {
              removeItems.push(c)
            }
          }
        })
        for (let i = 0; i < removeItems.length; i++) {
          try {
            await axios.delete(this.baseDomain + this.shopId + '/orders/' + this.order.id + '/items/' + removeItems[i].id + '.json?apikey=' + this.apiKey)
          } catch(e) {
            this.showError(e)
          }
        }
      }
    },
    editParticipantDialog (parentItem, option) {
      let defaultName = null
      if (typeof option === 'object') {
        defaultName = option.participant
      } else {
        defaultName = this.getParsedPackages(parentItem)[option].name
      }
      this.showDialog({
        title: this._('Participant'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Save'),
            class: 'success',
            icon: 'save',
            requiredFields: ['name'],
            handler: this.saveParticipant(parentItem, option)
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name',
            defaultValue: defaultName
          }          
        ]
      })
    },
    saveParticipant (parentItem, option) {
      return async (data) => {
        if (data && data.name && data.name !== option.participant) {
          try {
            let patchData = {
              id: parentItem.id,
              packages: this.updatePackageDescription(parentItem, option, data.name)
            }
            await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '/items/' + parentItem.id + '.json?apikey=' + this.apiKey, patchData)
          } catch(e) {
            console.log(e)
            this.showError(e)
          }
        }
      }
    },
    updatePackageDescription (parentItem, option, name) {
      let packages = JSON.parse(parentItem.packages)
      let participantIndex = null
      if (typeof option === 'object') {
        const itemRef = JSON.parse(option.packages).itemRef
        const pArr = itemRef.split('-')
        participantIndex = Number(pArr[0]) - 1
      } else {
        participantIndex = option
      }
      packages[participantIndex].name = name
      return JSON.stringify(packages)
    },
    async unlockOrder () {
      if (this.hasLockedItems) {
        try {
          let patchData = {
            id: this.order.id,
            unlockitems: true
          }
          await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, patchData)
        } catch(e) {
          // Fail silently for now
        }
      }
      this.getComponentLock(true)
    },
    formatChannexMessage (message) {
      return message
    },
    async displayChannexMessageDialog (message) {
      if (message && !parseInt(message.seen)) {
        let patchData = {
          id: message.id,
          seen: 1
        }
        try {
          await axios.patch(this.baseDomain + this.shopId + '/channexmessages/' + message.id + '.json?apikey=' + this.apiKey, patchData)
        } catch(e) {}
      }
      let fields = []
      if (message) {
        fields.push({
          type: 'info',
          name: 'msg',
          hideIcon: true,
          maxHeight: '300px',
          text: (message.sender === 'guest' ? '<strong>' + this.order.name + '</strong><br />' : '') + nl2br(message.message)
        })
      }
      fields.push({
        title: this._('Send message'),
        type: 'textarea',
        name: 'message'
      })
      this.showDialog({
        title: this._('Message') + (message ? ' ' + DateTime.fromISO(message.inserted_at).toFormat(this.dateFormat + ' ' + this.timeFormat) : ''),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Send'),
            class: 'success',
            icon: 'envelope',
            requiredFields: ['message'],
            handler: this.sendChannexMessage
          }
        ],
        fields
      })
    },
    async sendChannexMessage (data) {
      const postData = {
        message: data.message,
        booking_id: this.order.reference
      }
      try {
        await axios.post(this.baseDomain + this.shopId + '/channexmessages.json?apikey=' + this.apiKey, postData)
      } catch(e) {
        this.showError(e)
        return
      }
      this.showNotification(this._('The message has been sent.'))
      this.loadChannexMessages()
    },
    async convertToOpenOrder () {
      try {
        const data = {
          id: this.order.id,
          converttoopenorder: true
        }
        await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, data)
      } catch(error) {
        this.showError(error)
      }
    },
    expandAllDeliveries () {
      this.order.ownDelivery.forEach((delivery) => {
        this.openDeliveries[delivery.id] = true
      })
    },
    closeAllDeliveries () {
      this.order.ownDelivery.forEach((delivery) => {
        this.openDeliveries[delivery.id] = false
      })
    },
    isDeliverySelected(delivery) {
      return !!this.selectedDeliveries.find(i => i === delivery.id)
    },
    toggleDeliverySelection(delivery, newValue) {
      if (newValue) {
        if (!this.isDeliverySelected(delivery)) {
          this.selectedDeliveries.push(delivery.id)
        }
      } else {
        this.selectedDeliveries = this.selectedDeliveries.filter(v => v !== delivery.id)
      }
    },
    isItemRowSelected(item) {
      return !!this.selectedItems.find(i => i === item.id)
    },
    toggleItemRowSelection(item, newValue) {
      if (newValue) {
        this.selectedItems.push(item.id)
      } else {
        this.selectedItems = this.selectedItems.filter(v => v !== item.id)
      }
    },
    getItemComponent(item) {
      return !!item.reservation_datestart ? 'ReservationItem' : 'ProductItem'
    },
    toggleDelivery (delivery) {
      this.openDeliveries[delivery.id] = !this.openDeliveries[delivery.id]
    },
    isDeliveryOpen (delivery) {
      return this.openDeliveries[delivery.id]
    },
    createDeliveryDialog () {
      if (this.selectedDeliveries.length) {
        return this.combineDeliveriesDialog()
      }
      this.showDialog({
        title: this._('Create delivery'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['deliverymethod_id'],
            handler: this.createDelivery
          }
        ],
        fields: [
          {
            title: this._('Delivery method'),
            type: 'select',
            name: 'deliverymethod_id',
            options: this.allDeliverymethods.map(a => { return { value: a.id, name: a.name } }),
            defaultValue: this.allDeliverymethods[0].id
          }
        ]
      })
    },
    combineDeliveriesDialog () {
      if (!this.selectedDeliveries || !this.selectedDeliveries.length) {
        return
      }
      if (this.selectedDeliveries.length === 1) {
        return this.combineDeliveries({ delivery_id: this.selectedDeliveries[0] })
      }
      const selectedDeliveries = this.selectedDeliveries.map(d => this.order.ownDelivery.find(od => od.id === d))
      const deliveryOptions = selectedDeliveries.map(d => { return { value: d.id, name: d.deliverymethod.name + (parseInt(d.id) ? ' (' + d.id + ')': '') } })
      this.showDialog({
        title: this._('Combine selected'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['delivery_id'],
            handler: this.combineDeliveries
          }
        ],
        fields: [
          {
            title: this._('Choose which delivery to combine the selection into'),
            type: 'select',
            name: 'delivery_id',
            options: deliveryOptions,
            defaultValue: deliveryOptions[0].value
          }
        ]
      })
    },
    createDelivery (data) {
      const deliverymethodId = data.deliverymethod_id
      const deliverymethod = this.allDeliverymethods.find(dm => parseInt(dm.id, 10) === parseInt(deliverymethodId, 10))
      const newId = 'new-' + new Date().getTime()
      const delivery = {
        deliverymethod: deliverymethod,
        deliverymethod_id: deliverymethodId,
        id: newId,
        order_id: this.order.id,
        sharedTag: deliverymethod.sharedTag,
        ts: null
      }
      this.order.ownDelivery.push(delivery)
      for (let si of this.selectedItems) {
        const item = this.order.ownItem.find(i => parseInt(i.id, 10) === parseInt(si, 10))
        if (item) {
          item.delivery_id = newId
        }
      }
      this.selectedItems = []
      this.closeAllDeliveries()
      this.toggleDelivery(delivery)
    },
    combineDeliveries (data) {
      const deliveryId = data.delivery_id
      for (let item of this.order.ownItem) {
        if (this.selectedDeliveries.includes(item.delivery_id) || this.selectedItems.includes(item.id)) {
          item.delivery_id = deliveryId
        }
      }
      const deleteDeliveries = this.selectedDeliveries.filter(d => d !== deliveryId)
      const orphanDeliveries = this.order.ownDelivery.map(d => d.id).filter(d => !this.order.ownItem.map(i => i.delivery_id).includes(d))
      if (deleteDeliveries.length || orphanDeliveries.length) {
        this.order.ownDelivery = this.order.ownDelivery.filter(od => !deleteDeliveries.includes(od.id) && !orphanDeliveries.includes(od.id))
      }
      this.selectedDeliveries = []
      this.selectedItems = []
      this.closeAllDeliveries()
      this.toggleDelivery(this.order.ownDelivery.find(d => d.id === deliveryId))
    },
    deleteDelivery(delivery) {
      if (!delivery || !delivery.id || !this.order || !this.order.ownDelivery) {
        return
      }
      this.order.ownDelivery = this.order.ownDelivery.filter(d => d.id !== delivery.id)
      if (this.order.ownItem && this.order.ownItem.length) {
        for (let item of this.order.ownItem) {
          if (item.delivery_id === delivery.id) {
            item.delivery_id = null
          }
        }
      }
    },
    processMultipleDeliveries () {
      for (let id of this.selectedDeliveries) {
        this.processDelivery(id, 1)
      }
      this.selectedDeliveries = []
    },
    async processDelivery (id, status) {
      if (!this.order || !this.order.ownDelivery) {
        return
      }
      const delivery = this.order.ownDelivery.find(od => od.id === id)
      if (delivery && parseInt(delivery.processed) !== status) {
        let response
        try {
          const data = {
            id: id,
            processed: status
          }
          response = await axios.patch(this.baseDomain + this.shopId + '/deliveries/' + id + '.json?apikey=' + this.apiKey, data)
        } catch(e) {
          return
        }
        delivery.processed = status ? 1 : 0
      }
    },
    getParsedPackages(item) {
      const packages = JSON.parse(item.packages)
      packages.forEach(pkg => {
        pkg.participant = pkg.name
      })
      return packages
    },
    getItemOptions(item) {
      let items = this.allItems.filter(i => i.item_id === item.id)
      let sortedItems = []
      const indexDone = []
      if (item.packages && !item.item_id) { // Parent item
        let packagedItems = items.filter(i => i.packages && i.item_id)
        // Sort packaged items by participant and product index
        packagedItems.sort((a, b) => {
          const aRef = JSON.parse(a.packages).itemRef.split('-').map(Number)
          const bRef = JSON.parse(b.packages).itemRef.split('-').map(Number)
          return aRef[0] - bRef[0] || aRef[1] - bRef[1]
        })
        let childItems = []
        const packages = JSON.parse(item.packages)
        if (packages && packages.forEach) {
          packages.forEach((participant, participantIndex) => {
            packagedItems.forEach(subItem => {
              const subItemPackages = JSON.parse(subItem.packages)
              participant.products.forEach(product => {
                if (subItemPackages.itemRef === product.itemRef) {
                  if (!indexDone[participantIndex]) {
                    childItems.push({ ...subItem, participant: participant.name })
                    indexDone[participantIndex] = true
                  } else {
                    childItems.push(subItem)
                  }
                }
              })
            })
          })
        }
        sortedItems.push(...childItems, ...items.filter(i => !i.packages))
        items = sortedItems
      }
      for (let i = 0; i < items.length; i++) {
        let children = this.getItemOptions(items[i])
        if (children && children.length) {
          items.splice(i + 1, 0, ...children)
        }
      }
      return items
    },
    modifyOrder () {
      if (this.hasLockedItems) {
        this.showDialog({
          title: this._('The payment link has already been activated. Unlock the order for modification?'),
          text: this._('Please make sure that the customer reloads the payment link after your modifications.'),
          buttons: [
            {
              title: this._('Cancel'),
              icon: 'ban',
              handler: () => {}
            },
            {
              title: this._('Ok'),
              class: 'danger',
              icon: 'unlock',
              handler: () => {
                this.EventBus.$emit('modifyOrder', {
                  order: this.order
                })
              }
            }, 
          ]
        })
      } else {
        this.EventBus.$emit('modifyOrder', {
          order: this.order
        })
      }
    },
    async modifyOrderAsNew () {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/orders.json?combine=' + this.order.id + '&apikey=' + this.apiKey)
      } catch(e) {
        this.showError(e)
        return
      }
      let newId = this.component.initData.orderId = response.data
      await this.loadOrder(newId)
      this.modifyOrder()
    },
    isPriorityCountry (id) {
      return ['SE', 'NO', 'DK', 'EE', 'RU'].indexOf(id) !== -1
    },
    getCountryName(id) {
      return this.countries && this.countries[id] ? this.countries[id] : id
    },
    getTagTitle(tag) {
      if (!tag || !tag.title) {
        return ''
      }
      const parts = tag.title.split(':::')
      return parts.pop()
    },
    addTag(tag) {
      if (!tag) {
        return
      }
      if (typeof tag === 'object') {
        if (tag.id) {
          this.order.sharedTag.push({
            id: tag.id,
            title: tag.value
          })
        } else {
          this.order.sharedTag.push({
            title: 'order:::' + tag.title
          })
        }
      } else {
        this.order.sharedTag.push({
          title: 'order:::' + tag
        })
      }
      nextTick(() => {
        this.tagToAdd = ''
      })
    },
    removeTag(tag) {
      this.order.sharedTag = this.order.sharedTag.filter(t => t.title !== tag.title)
    },
    getEmailEventType (type) {
      switch (type) {
        case 'SpamComplaint':
          return this._('Spam complaint')
        case 'Delivery':
          return this._('Delivered')
        case 'Bounce':
        default:
          return this._(type)
      }
    },
    async displayMessage (message) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?message=' + message.identifier + '&apikey=' + this.apiKey)
      } catch(e) {
        return
      }
      const msg = response.data
      this.showModal('ordermessagedetails', SentMessageDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          originalMessage: msg
        },
        events: {},
        handlers: {},
        width: 800
      })
    },
    formatTermsType (type) {
      switch (type) {
        case 'product':
          return this._('Product-specific terms')
        case 'delivery':
          return this._('Delivery terms')
        default:
          return this._(type)
      }
    },
    displayTerms (terms) {
      this.showDialog({
        title: terms.name,
        width: 800,
        buttons: [
          {
            title: this._('Close'),
            icon: 'close',
            handler: () => {}
          },
        ],
        fields: [
          {
            title: '',
            type: 'textarea',
            editor: 'none',
            name: 'terms',
            defaultValue: terms.text
          }
        ]
      })
    },
    displayResource (resource) {
      this.showModal('orderresourcedetails', ResourceDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              resourceId: resource.id,
              modal: true
            }
          }
        },
        events: {},
        handlers: {},
        width: 1000
      })
    },
    displayReservation (reservation) {
      if (reservation) {
        this.showModal('orderreservationdetails', ReservationDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              initData: {
                reservationId: reservation.id,
                orderId: this.order.id,
                modal: true
              }
            }
          },
          events: {},
          handlers: {},
          width: 1000
        })
      }
    },
    async displayPaymentLink () {
      let originalOrder = this.order
      if (this.order && this.order.paymentplan && this.order.id != this.mainOrderId) {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/orders/' + this.mainOrderId + '.json?items=true&deliveries=true&vatbreakdown=true&reservations=true&bamboraid=true&messages=true&acceptedterms=true&verifiedcustomer=true&apikey=' + this.apiKey)
          originalOrder = response.data
        } catch (e) { // Fail silently and show the original order
        }
      }
      this.showModal('paymentlink', PaymentLink, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          locale: this.locale,
          versions: this.settings.versions.split(';'),
          originalOrder: originalOrder,
          settings: this.settings
        },
        backgroundEvents: {
          expirationDateSet: this.expirationDateSet,
          paymentLinkSent: this.expirationDateSet
        },
        handlers: {},
        width: 800
      })
    },
    displayConfirmationLink () {
      this.showModal('confirmationlink', ConfirmationLink, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          locale: this.locale,
          versions: this.settings.versions.split(';'),
          originalOrder: this.order,
          settings: this.settings
        },
        backgroundEvents: {
          confirmationLinkSent: this.expirationDateSet
        },
        handlers: {},
        width: 800
      })
    },
    async expirationDateSet () {
      try {
        await this.loadOrder(this.order.id)
      } catch(e) {}
    },
    displayValuecard (valuecard) {
      this.showModal('valuecarddetails', ValuecardDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          locale: this.locale,
          valuecard: valuecard,
          order: this.order,
          settings: this.settings
        },
        handlers: {},
        width: 800
      })
    },
    displayInvoice (force = false) {
      if (this.isModal) {
        this.showModal('orderinvoicedetails', InvoiceDetails, {
          props: {
            baseDomain: this.baseDomain,
            shopId: this.shopId,
            apiKey: this.apiKey,
            component: {
              initData: {
                invoiceId: this.invoice.id,
                modal: true
              }
            }
          },
          events: {},
          handlers: {},
          width: 1000
        })
      } else {
        this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'InvoiceDetails',
          title: this._('Transaction') + ' ' + this.invoice.id + (this.invoice.name ? ': ' + this.invoice.name : ''),
          width: 6,
          data: {
            invoiceId: this.invoice.id,
            trail: this.componentTrail
          }
        })
      }
    },
    displayCustomer () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CustomerDetails',
        title: this._('Customer') + ' ' + this.order.customer_id + (this.order.name ? ': ' + this.order.name : ''),
        width: 6,
        data: {
          customerId: this.order.customer_id,
          trail: this.componentTrail,
          activateTab: 'general'
        }
      })
    },
    displayBillingCustomer () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'BillingCustomerDetails',
        title: this._('Billing Customer') + ' ' + this.order.billingcustomer_id + (this.order.billing_company ? ': ' + this.order.billing_company : ''),
        width: 6,
        data: {
          billingCustomerId: this.order.billingcustomer_id,
          trail: this.componentTrail,
          activateTab: 'general'
        }
      })
    },
    displayConsolidatedOrder () {
      this.displayOrder(this.order.consolidate_id)
    },
    displayOrder (id) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ' ' + id,
        width: 6,
        data: {
          orderId: id,
          trail: this.componentTrail,
          activateTab: 'general'
        }
      })
    },
    folioItemTotal (item) {
      const resourceCount = parseInt(item.resource_count, 10) > 1 ? parseInt(item.resource_count, 10) : 1
      return (parseFloat(item.price) || 0) * (parseFloat(item.count) || 0) * resourceCount
    },
    startFolioSelect () {
      this.selectedFolioItems = []
      this.folioSelectMode = true
    },
    cancelFolioSelect () {
      this.selectedFolioItems = []
      this.folioSelectMode = false
    },
    isFolioItemSelected (item) {
      return this.selectedFolioItems.indexOf(item.id) !== -1
    },
    toggleFolioItem (item) {
      if (this.isFolioItemSelected(item)) {
        this.selectedFolioItems = this.selectedFolioItems.filter(id => id !== item.id)
      } else {
        this.selectedFolioItems = [...this.selectedFolioItems, item.id]
      }
    },
    // The Paid / Not paid badge of a payment plan's or folio's order tab
    paidBadge (status) {
      if (!status) {
        return {}
      }
      return status.paid ? { badge: this._('Paid'), badgeClass: 'btn-success' } : { badge: this._('Not paid'), badgeClass: 'btn-danger' }
    },
    openFolioOrder (folio) {
      if (!folio || parseInt(folio.id) === parseInt(this.order.id)) {
        return
      }
      this.component.initData.orderId = folio.id
      this.loadOrder(folio.id)
    },
    openFolioTab (id) {
      this.openFolioOrder(this.folioOrders.find(folio => String(parseInt(folio.id)) === id))
    },
    moveToFolioDialog () {
      if (!this.selectedFolioItems.length) {
        return
      }
      const buttons = [
        {
          title: this._('Cancel'),
          icon: 'ban',
          handler: () => {}
        },
        {
          title: this._('New folio'),
          class: 'primary',
          icon: 'plus',
          handler: () => this.moveSelectedItemsToFolio({ createfolio: 1 })
        },
        {
          title: this._('New folio for another customer'),
          class: 'primary',
          icon: 'user',
          handler: () => this.selectFolioCustomer()
        }
      ]
      this.folioOrders.filter(folio => parseInt(folio.id) !== parseInt(this.order.id) && !parseInt(folio.paid)).forEach(folio => {
        buttons.push({
          title: this._('Order') + ' ' + folio.id,
          class: 'info',
          icon: 'share',
          handler: () => this.moveSelectedItemsToFolio({ moveitems: this.selectedFolioItems, targetorder_id: folio.id })
        })
      })
      this.showDialog({
        title: this._('Move to folio'),
        width: 640,
        buttons
      })
    },
    selectFolioCustomer () {
      this.showModal('foliocustomerselector', CustomerSelector, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          mode: this.order && this.order.billingcustomer_id ? 'billing' : 'client'
        },
        events: {
          select: async (data) => {
            const payload = { createfolio: 1 }
            if (data.customer && data.customer.id) {
              payload.customer_id = data.customer.id
            }
            if (data.billingCustomer && data.billingCustomer.id) {
              payload.billingcustomer_id = data.billingCustomer.id
            }
            await this.moveSelectedItemsToFolio(payload)
          }
        },
        handlers: {},
        width: 1000
      })
    },
    async moveSelectedItemsToFolio (payload) {
      const data = Object.assign({
        id: this.order.id
      }, payload)
      if (payload.createfolio) {
        data.moveitems = this.selectedFolioItems
      }
      try {
        await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, data)
      } catch (e) {
        return this.showError(e)
      }
      this.cancelFolioSelect()
      await this.loadOrder(this.order.id)
    },
    editCustomerDetails () {
      this.customerDetailsEditable = true
    },
    closeCustomerDetails () {
      this.customerDetailsEditable = false
    },
    editBillingCustomerDetails () {
      this.billingCustomerDetailsEditable = true
    },
    closeBillingCustomerDetails () {
      this.billingCustomerDetailsEditable = false
    },
    printInvoice () {
      window.open(this.baseDomain + this.shopId + (this.order.version ? '/' + this.order.version : '' ) + '/bills/' + this.order.identifier + '.pdf')
    },
    printReceipt () {
      window.open(this.baseDomain + this.shopId + (this.order.version ? '/' + this.order.version : '' ) + '/receipts/' + this.order.identifier + '.pdf')
    },
    printTransfer () {
      window.open(this.baseDomain + this.shopId + (this.order.version ? '/' + this.order.version : '' ) + '/transfers/' + this.order.identifier + '.pdf')
    },
    printRefund () {
      window.open(this.baseDomain + this.shopId + (this.order.version ? '/' + this.order.version : '' ) + '/refunds/' + this.order.identifier + '.pdf')
    },
    printDeliveryManifest () {
      window.open(this.baseDomain + this.shopId + (this.order.version ? '/' + this.order.version : '' ) + '/orders/' + this.order.identifier + '.pdf')
    },
    refund (skipBambora) {
      this.showDialog({
        title: this._('Are you sure you want to refund the invoice? Refunding can\'t be undone.'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.refundInvoice(skipBambora)
            }
          }, 
        ]
      })
    },
    refundInvoice (skipBambora) {
      return new Promise(async (resolve, reject) => {
        let response
        this.refunding = true
        try {
          const data = {
            identifier: this.order.identifier
          }
          response = await axios.post(this.baseDomain + this.shopId + '/refunds/new.json?' + (skipBambora ? 'skipbambora=true&' : 'localmerchant=true&') + 'apikey=' + this.apiKey, data)
          this.showNotification(this._('Refund completed!'))
          await this.loadOrder(this.order.id)
          resolve(response)
        } catch(e) {
          this.showError(this._('Refund failed!'))
          reject(e)
        } finally {
          this.refunding = false
        }
      })
    },
    sendAftersalesMessage() {
      this.sendConfirmation('aftersales', (result) => {
        if (result) {
          this.order.aftersales_sent = '1'
        }
      })
    },
    sendProcessConfirmation() {
      this.sendConfirmation('processconfirmation')
    },
    sendOrderCancellation() {
      this.sendConfirmation('ordercancellation')
    },
    sendOrderCancellationWithNotification() {
      this.sendConfirmation('ordercancellation', (success) => {
        if (success) {
          this.showNotification(this._('Message sent!'))
        }
      })
    },
    async sendConfirmation(type, callback) {
      let response
      let versionStr = this.versionStr
      if (this.order && this.order.version) {
        versionStr = '&version=' + this.order.version
      }
      try {
        response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/email/' + type + '/' + this.order.id + '.json?apikey=' + this.apiKey + versionStr)
        if (callback) {
          callback(true)
        }
      } catch(e) {
        this.showError(this._('Sending the message failed!'))
        if (callback) {
          callback(false)
        }
        return
      }
    },
    archiveDialog () {
      this.showDialog({
        title: this._('Are you sure you want to archive the order?'),
        text: this.isGygOrder ? this._('Cancel this booking in GetYourGuide\'s Supplier Portal too; GetYourGuide is not told about cancellations made in Johku.') : '',
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Archive and remove reservations'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.archive(true)
            }
          },
          {
            title: this._('verb|Archive'),
            class: 'warning',
            icon: 'ban',
            handler: () => {
              this.archive()
            }
          }
        ]
      })
    },
    archive (removeReservations = false) {
      this.setArchiveStatus('1', removeReservations)
    },
    restore () {
      this.setArchiveStatus('0')
    },
    async setArchiveStatus (archived, removeReservations = false) {
      let response
      const data = {
        id: this.order.id,
        archived,
        removereservations: removeReservations ? 1 : 0
      }
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        return this.showError(e)
      }
      this.close()
    },
    async remove () {
      if (!this.isRemoveButtonEnabled) {
        return false
      }
      this.showDialog({
        title: this._('Are you sure you want to delete the order? Deletion can\'t be undone.'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Delete order and send cancellation message'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.startRemoveOrder(true)
            }
          }, 
          {
            title: this._('Delete order'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.startRemoveOrder(false)
            }
          }
        ]
      })
    },
    async startRemoveOrder (sendOrderCancellation) {
      if (sendOrderCancellation) {
        this.sendOrderCancellation()
      }
      this.removeOrder()
    },
    async removeOrder () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/orders/' + this.order.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        this.showError(this._('Deleting the order failed!'))
        return
      }
      this.showNotification(this._('Order deleted!'))
      this.close()
    },
    confirmDialog (payment) {
      if (!this.isApproveButtonEnabled) {
        return false
      }
      let msg = this._('Are you sure you want to create the invoice now?')
      let willActivateAutomatically = false
      switch (payment) {
        case 'adminnone':
          msg = this._('Are you sure you want to approve the order now?')
          break
        case 'confirm': // Confirmed orders will do their own thing
          msg = this._('Are you sure you want to confirm the order now?')
          break
        case 'admincash': // Cash, card, bill => these will become paid, so we will activate the services in any case
        case 'admincard':
        case 'bill':
          willActivateAutomatically = true
          break
        case 'none': // None => bill with no payment, so let's not activate by default
          break
      }
      const fields = [
        {
          title: this._('Send notifications'),
          type: 'checkbox',
          name: 'notifications',
          defaultValue: '1'
        }
      ]
      if (payment !== 'confirm') {
        if (willActivateAutomatically) {
          fields.push({
            text: this._('Activate sold services immediately'),
            type: 'info',
            name: 'activate'
          })
        } else {
          fields.push({
            title: this._('Activate sold services immediately'),
            type: 'checkbox',
            name: 'activate',
            defaultValue: '0'
          })
        }
      }
      this.showDialog({
        title: msg,
        fields: fields,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Yes'),
            class: 'success',
            icon: 'check-circle',
            handler: (data) => {
              // Third parameter is skipNotifications, fourth is activateServices
              // So we do one ! to negate the notifications value and two ! to cast the activateServices to a boolean
              this.save(true, payment, !parseInt(data.notifications), !!parseInt(data.activate))
            }
          }
        ]
      })
    },
    getSaveData () {
      const exportFields = [
        'id', 'address', 'aftersales_sent', 'city', 'company', 'completed', 'consolidated', 'country', 'customer_id', 'datestart', 'delivery', 'deliverycount', 'email', 'expirationdate',
        'evoice', 'evoiceint', 'groupdeal', 'ident', 'identifier', 'interest', 'mark', 'master', 'more', 'name', 'notes', 'orderdate', 'origin', 'paid', 'pay_option1_duedays',
        'payment', 'paymentdate', 'phone', 'processdate', 'processed', 'reference', 'refunded', 'refundidentifier', 'skip_notifications', 'src', 'stripecustomer',
        'stripesource', 'trackingcode', 'transactionid', 'version', 'yourreference', 'zip',
        'billingcustomer_id', 'billing_company', 'billing_department', 'billing_address', 'billing_zip', 'billing_city', 'billing_country', 
        'billing_paymentterm', 'billing_email', 'billing_billingemail', 'billing_ident', 'billing_evoice', 'billing_evoiceint', 'billing_name', 'billing_phone',
        'sharedTag', 'paymentplan'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.order[e], a), {})
      if (this.isFeatureEnabled('delivery2.0')) {
        data.deliveryRules = this.getDeliveryRules()
      }
      data.source = 'editor'
      return data
    },
    getDeliveryRules () {
      if (!this.order || !this.order.ownDelivery || !this.order.ownDelivery.length) {
        return
      }
      const rules = []
      for (let delivery of this.order.ownDelivery) {
        const dm = Object.assign({}, delivery)
        dm.items = this.order.ownItem.filter(i => i.delivery_id === delivery.id).map(i => i.id)
        rules.push(dm)
      }
      return rules
    },
    async save (close = true, payment = false, skipNotifications = false, activateServices = false) {
      if (!this.isSaveButtonEnabled) {
        return false
      }
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/orders/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          this.saving = false
          return
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (parseInt(this.processconfirmation)) {
        this.sendProcessConfirmation()
      }
      if (response && response.data && response.data.id) {
        await this.loadOrder(response.data.id)
      }
      this.saving = false
      this.storeState()
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.order)
      if (payment) {
        switch (payment) {
          case 'confirm':
            await this.confirmOrder(this.order, skipNotifications)
            break
          case 'admincard':
          case 'admincash':
          case 'bill':
          case 'none':
          case 'adminnone':
            await this.convertToBill(this.order, payment, skipNotifications, activateServices)
            break
        }
      }
      if (close) {
        this.close()
      }
    },
    confirmOrder (order, skipNotifications) {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          const data = {
            id: order.id,
            confirm: 1,
            notifications: skipNotifications ? 0 : 1
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.put(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
        }
        resolve()
      })
    },
    convertToBill (order, payment, skipNotifications, activateServices) {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          const data = {
            id: order.id,
            converttobill: 1,
            payment: payment,
            notifications: skipNotifications ? 0 : 1,
            services: activateServices ? 1 : 0
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.put(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
        }
        resolve()
      })
    },
    activateValueCardsDialog () {
      this.showDialog({
        title: this._('Activate value cards'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Activate'),
            class: 'success',
            icon: 'check',
            handler: this.activateValueCards
          }
        ],
      })
    },
    activateValueCards () {
      return new Promise(async (resolve, reject) => {
        this.showNotification(this._('Processing, please wait...'))
        let response
        const order = this.order
        try {
          const data = {
            id: order.id,
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?activatevaluecards=true&apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
          return reject()
        }
        this.loadOrder(order.id)
        resolve()
      })
    },
    resendValueCardEmailDialog () {
      this.showDialog({
        title: this._('Resend value card email'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Send email'),
            class: 'success',
            icon: 'send',
            requiredFields: ['email'],
            handler: this.resendValueCardEmail
          }
        ],
        fields: [
          {
            title: this._('Email'),
            type: 'email',
            name: 'email',
            defaultValue: this.order.email ? this.order.email : ''
          }
        ]
      })
    },
    resendValueCardEmail (params) {
      return new Promise(async (resolve, reject) => {
        this.showNotification(this._('Processing, please wait...'))
        let response
        const order = this.order
        try {
          const data = {
            id: order.id,
            overrideEmail: params.email
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?resendvaluecards=true&apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
          return reject()
        }
        this.showNotification(this._('Message sent!'))
        resolve()
      })
    },
    resendTicketEmailDialog () {
      this.showDialog({
        title: this._('Resend ticket email'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Send email'),
            class: 'success',
            icon: 'send',
            requiredFields: ['email'],
            handler: this.resendTicketEmail
          }
        ],
        fields: [
          {
            title: this._('Email'),
            type: 'email',
            name: 'email',
            defaultValue: this.order.email ? this.order.email : ''
          }
        ]
      })
    },
    resendTicketEmail (params) {
      return new Promise(async (resolve, reject) => {
        this.showNotification(this._('Processing, please wait...'))
        let response
        const order = this.order
        try {
          const data = {
            id: order.id,
            overrideEmail: params.email
          }
          let versionStr = this.versionStr
          if (order.version) {
            versionStr = '&version=' + order.version
          }
          response = await axios.post(this.baseDomain + this.shopId + (this.versionStr ? '/' + this.version : '') + '/orders/' + order.id + '.json?resendtickets=true&apikey=' + this.apiKey + versionStr, data)
        } catch(e) {
          this.showError(e)
          return reject()
        }
        this.showNotification(this._('Message sent!'))
        resolve()
      })
    },
    throttledLoadOrder: throttle(function (id) { this.loadOrder(id) }, 1000, { leading: false, trailing: true }),
    initRequestDrafts () {
      const drafts = {}
      for (const request of this.orderRequests) {
        drafts[request.id] = this.getRequestNotes(request)
      }
      this.requestNotesDrafts = drafts
    },
    getRequestNotes (request) {
      return request.notes || request.merchant_response || ''
    },
    formatRequestType (type) {
      return type === 'move' ? this._('Move request') : this._('Cancellation request')
    },
    formatRequestStatus (status) {
      if (status === 'processed') {
        return this._('Processed')
      }
      if (status === 'denied') {
        return this._('Denied')
      }
      return this._('Pending')
    },
    requestStatusClass (status) {
      if (status === 'processed') {
        return 'label-success'
      }
      if (status === 'denied') {
        return 'label-danger'
      }
      return 'label-warning'
    },
    formatRequestTimestamp (ts) {
      if (!ts) {
        return ''
      }
      return this.$filters.timestampToDate(ts) + ' ' + this.$filters.timestampToTime(ts)
    },
    formatRequestTimestampRange (startTs, endTs) {
      if (!startTs) {
        return ''
      }
      const start = parseInt(startTs, 10)
      const end = endTs ? parseInt(endTs, 10) : 0
      if (!end || end === start) {
        return this.formatRequestTimestamp(start)
      }
      const startDate = this.$filters.timestampToDate(start)
      const endDate = this.$filters.timestampToDate(end)
      const startTime = this.$filters.timestampToTime(start)
      const endTime = this.$filters.timestampToTime(end)
      if (startDate === endDate) {
        return startDate + ' ' + this._('at') + ' ' + startTime + ' - ' + endTime
      }
      return startDate + ' ' + this._('at') + ' ' + startTime + ' - ' + endDate + ' ' + this._('at') + ' ' + endTime
    },
    parseRequestItems (request) {
      if (!request || !request.items) {
        return []
      }
      if (typeof request.items === 'string') {
        try {
          const parsed = JSON.parse(request.items)
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          return []
        }
      }
      return Array.isArray(request.items) ? request.items : []
    },
    async processOrderRequest (request, status) {
      try {
        await axios.patch(this.baseDomain + this.shopId + '/orderrequests/' + request.id + '.json?apikey=' + this.apiKey, {
          id: request.id,
          status,
          notes: this.requestNotesDrafts[request.id] || ''
        })
        this.showNotification(this._('Request updated successfully!'))
        await this.loadOrder(this.order.id)
      } catch (e) {
        this.showError(e)
      }
    },
    loadOrder (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/orders/' + id + '.json?items=true&deliveries=true&vatbreakdown=true&reservations=true&bamboraid=true&messages=true&acceptedterms=true&verifiedcustomer=true&requests=true&apikey=' + this.apiKey)
        } catch (e) {
          this.loading = false
          if (e.response && e.response.status === 404) {
            this.close()
          } else {
            this.showError(e)
          }
          reject()
          return
        }
        this.order = response.data
        this.cancelFolioSelect()
        this.initRequestDrafts()
        // this.originalState = JSON.stringify({ order: this.order })
        this.processed = this.order.processed
        try {
          await this.loadInvoice(this.order.identifier)
        } catch(e) {
          this.invoice = null
          // No need to reject() since we are just checking if the invoice exists
        }
        this.channexMessages = []
        const loaders = []
        loaders.push(this.loadOriginalOrders(this.order.id))
        loaders.push(this.loadTags())
        loaders.push(this.loadDeliveryTags())
        loaders.push(this.loadDeliverymethods())
        loaders.push(this.loadNotificationEvents(this.order.id))
        if (this.order.reference && (this.order.origin === 'channex-boo' || this.order.origin === 'channex-air')) {
          loaders.push(this.loadChannexMessages())
        }
        await Promise.allSettled(loaders)
        this.storeState()
        this.loading = false
        resolve(this.order)
      })
    },
    loadInvoice (identifier) {
      return new Promise(async (resolve, reject) => {
        let response
        this.invoice = null
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/bills/' + identifier + '.json?details=true&apikey=' + this.apiKey)
        } catch (e) {
          reject()
          return
        }
        this.invoice = response.data
        resolve(this.invoice)
      })
    },
    loadOriginalOrders (id) {
      return new Promise(async (resolve, reject) => {
        let response
        this.originalOrders = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/orders/' + id + '.json?originalorders=true&apikey=' + this.apiKey)
        } catch (e) {
          this.showError(e)
          reject()
          return
        }
        this.originalOrders = response.data
        resolve(this.originalOrders)
      })
    },
    loadTags () {
      return new Promise(async (resolve, reject) => {
        let response
        this.allTags = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/tags.json?type=order&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          this.showError(e)
          reject()
          return
        }
        this.allTags = response.data
        resolve(this.allTags)
      })
    },
    loadDeliveryTags () {
      return new Promise(async (resolve, reject) => {
        let response
        this.allDeliveryTags = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/tags.json?type=delivery&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          this.showError(e)
          reject()
          return
        }
        this.allDeliveryTags = response.data
        resolve(this.allDeliveryTags)
      })
    },
    loadDeliverymethods () {
      return new Promise(async (resolve, reject) => {
        let response
        this.allDeliverymethods = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/deliverymethods.json?tags=true&shipitmethods=true&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          this.showError(e)
          reject()
          return
        }
        this.allDeliverymethods = response.data
        resolve(this.allDeliverymethods)
      })
    },
    loadChannexMessages () {
      return new Promise(async (resolve, reject) => {
        let response
        this.channexMessages = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/channexmessages/' + this.order.reference + '.json?apikey=' + this.apiKey)
        } catch(e) {
          resolve([]) // Fail silently in case the messages won't load
          return
        }
        this.channexMessages = response.data
        resolve(this.channexMessages)
      })
    },
    loadNotificationEvents (orderId) {
      return new Promise(async (resolve, reject) => {
        let response
        this.notificationEvents = []
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/notificationschedule.json?orderid=' + orderId + '&apikey=' + this.apiKey + '&rawformat=true')
        } catch(e) {
          resolve([]) // Fail silently in case the notifications won't load
          return
        }
        this.notificationEvents = (response.data || []).sort((a, b) => Number(b.ts) - Number(a.ts))
        resolve(this.notificationEvents)
      })
    },
    notificationEventStatus (status) {
      const _ = this._
      switch (parseInt(status, 10)) {
        case 0:
          return _('Not sent')
        case 1:
          return _('Sent')
        case 2:
          return _('Send failed')
        case 3:
          return _('Not paid')
        case 4:
          return _('Skipped')
        case 5:
          return _('SMS service disabled')
        case 6:
          return _('No PIN found')
        case 7:
          return _('No reservation found')
        case 8:
          return _('Not confirmed')
        case 9:
          return _('No checkin/checkout')
      }
      return status
    },
    notificationEventRecipient (event) {
      if (parseInt(event.type) === 1) {
        if (event.custom_numbers) {
          return event.custom_numbers.split(';').join(', ')
        }
        return event.phone
      }
      if (event.custom_emails) {
        return event.custom_emails.split(';').join(', ')
      }
      return event.email
    },
    displayNotificationEvent (event) {
      this.showModal('notificationdetails', ANotificationDetails, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              notificationId: event.id,
              notification: event,
              modal: true
            }
          }
        },
        events: {},
        handlers: {
          closed: (e) => {
            this.loadNotificationEvents(this.order.id)
          }
        },
        width: 1000
      })
    },
    getPaymentPlanType (type) {
      switch (type) {
        case 'advance':
          return this._('Advance payment')
        case 'recurring':
          return this._('Recurring payment')
        default:
          return type
      }
    },
    addPaymentPlan () {
      this.paymentPlan = {
        type: 'advance',
        amount_type: 'fixed',
        amount: 0,
        final_payment_type: 'fixed',
        final_payment_date: null
      }
    },
    removePaymentPlan () {
      this.paymentPlan = null
    }
  },
  components: {
    HubOrderPane,
    DeliveryItemRow,
    DeliveryRowDetails,
    DeliveryRowTitle,
    ProductItem,
    ReservationItem,
    PaymentPlanSelector,
    SubscriptionsPanel
  },
  async created () {
    if (this.component && this.component.initData) {
      try {
        await this.loadOrder(this.component.initData.orderId)
      } catch(e) {}
    }
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (!this.deleted && this.order && this.order.id && parseInt(this.order.id, 10) !== parseInt(this.component.initData.orderId, 10)) {
        try {
          await this.loadOrder(this.component.initData.orderId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['orderUpdate'] = async (message) => {
        if (!this.deleted && message && message.data && this.order && parseInt(message.data.id, 10) === parseInt(this.order.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            try {
              await this.throttledLoadOrder(message.data.id)
            } catch(e) {}
          }
        }
      }
      events['reservationUpdate'] = async (message) => {
        if (!this.deleted && this.order && message && message.data && this.reservationIds.includes(parseInt(message.data.id, 10))) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            try {
              await this.throttledLoadOrder(this.order.id)
            } catch(e) {}
          }
        }
      }
      events['deliveryUpdate'] = async (message) => {
        if (!this.deleted && this.order && message && message.data && this.deliveryIds.includes(parseInt(message.data.id, 10))) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            try {
              await this.throttledLoadOrder(this.order.id)
            } catch(e) {}
          }
        }
      }
      events['channexmessageUpdate'] = async (message) => {
        if (this.order && this.order.reference && (this.order.origin === 'channex-boo' || this.order.origin === 'channex-air')) {
          this.loadChannexMessages()
        }
      }
      this.events = events
    }
    this.EventBus.$on('reservationRemove', (data) => {
      this.order.ownItem.forEach(item => {
        item.ownReservation = item.ownReservation.filter(reservation => parseInt(reservation.id) !== parseInt(data.id))
      })
    })
    this.EventBus.$on('selectDelivery', (delivery) => {
      if (!this.isDeliverySelected(delivery)) {
        this.selectedDeliveries.push(delivery.id)
      }
    })
    this.EventBus.$on('deselectDelivery', (delivery) => {
      this.selectedDeliveries = this.selectedDeliveries.filter(v => v !== delivery.id)
    })
    this.EventBus.$on('selectDeliveryItem', (item) => {
      if(!this.isItemRowSelected(item)) {
        this.selectedItems.push(item.id)
      }
    })
    this.EventBus.$on('deselectDeliveryItem', (item) => {
      this.selectedItems = this.selectedItems.filter(v => v !== item.id)
    })
    this.enableEventBus()
  },
  unmounted () {
    this.EventBus.$off('reservationRemove')
  }
}

</script>

<style scoped>
.order-conversation {
  /* HubOrderPane fits its height to the space above the save bar */
  min-height: 320px;
}

.rightbuttons {
  float: right;
}
.table-borderless th, .table-borderless td {
  border-width: 0;
}
.participant-title {
  background-color: #d9edf7;
  vertical-align: middle;
}
tr > td.participant-title:first-child {
  padding-left: 15px;
}
.folio-toolbar {
  margin-top: 12px;
  text-align: right;
}
.folio-panel {
  margin-top: 16px;
}
.folio-panel-heading, .folio-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.folio-actions {
  display: flex;
  gap: 8px;
}
.folio-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  cursor: pointer;
  user-select: none;
}
.folio-item:hover, .folio-item:focus {
  background-color: #f5f5f5;
  outline: none;
}
.folio-item.folio-item-selected {
  background-color: #d9edf7;
}
.folio-item input[type="checkbox"] {
  margin: 0;
  pointer-events: none;
}
.folio-item-name {
  flex: 1 1 auto;
}
.folio-item-price {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
</style>